// @ts-nocheck
// QuestionnaireModal.tsx - Updated with Bug Fixes (AI-127, AI-132, AI-133)
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Dog, Cat, Building, Phone, Globe, Upload, CheckCircle, AlertCircle, Info, PawPrint, Calendar } from 'lucide-react';
import DocumentUploadView from '../documents/DocumentUploadView';
import AttestationBox from '../common/AttestationBox';
import LoadingSpinner from '../common/LoadingSpinner';
import { petService } from '../../api/petService';
import { residentService, parsePhoneNumber, type ResidentInfo } from '../../api/residentService';
import { settingsService } from '../../api/settingsService';
import {
  PET_REGISTRATION_ATTESTATION_TEXT,
  PET_REGISTRATION_ATTESTATION_TITLE,
  buildAttestationData,
  type AttestationData,
} from '../../constants/attestation';
import { hasPersistedPetPhotoPreview } from '../../utils/petPhotoUpload';
import { uploadFileToPet } from '../../utils/directS3Upload';
import { leaseCapBlockedMessage, leaseCapFullDraftHint } from '../../utils/leasePetCapMessages';
import { mergeRestoredDraftDocuments, restoredDraftDocuments } from '../../utils/draftDocumentRestore';

// Custom SVG Icons
const CatIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="icon icon-tabler icons-tabler-outline icon-tabler-cat"
  >
    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
    <path d="M20 3v10a8 8 0 1 1 -16 0v-10l3.432 3.432a7.963 7.963 0 0 1 4.568 -1.432c1.769 0 3.403 .574 4.728 1.546l3.272 -3.546z" />
    <path d="M2 16h5l-4 4" />
    <path d="M22 16h-5l4 4" />
    <path d="M12 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
    <path d="M9 11v.01" />
    <path d="M15 11v.01" />
  </svg>
);

const PawIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="icon icon-tabler icons-tabler-outline icon-tabler-paw"
  >
    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
    <path d="M14.7 13.5c-1.1 -2 -1.441 -2.5 -2.7 -2.5c-1.259 0 -1.736 .755 -2.836 2.747c-.942 1.703 -2.846 1.845 -3.321 3.291c-.097 .265 -.145 .677 -.143 .962c0 1.176 .787 2 1.8 2c1.259 0 3 -1 4.5 -1s3.241 1 4.5 1c1.013 0 1.8 -.823 1.8 -2c0 -.285 -.049 -.697 -.146 -.962c-.475 -1.451 -2.512 -1.835 -3.454 -3.538z" />
    <path d="M20.188 8.082a1.039 1.039 0 0 0 -.406 -.082h-.015c-.735 .012 -1.56 .75 -1.993 1.866c-.519 1.335 -.28 2.7 .538 3.052c.129 .055 .267 .082 .406 .082c.739 0 1.575 -.742 2.011 -1.866c.516 -1.335 .273 -2.7 -.54 -3.052z" />
    <path d="M9.474 9c.055 0 .109 0 .163 -.011c.944 -.128 1.533 -1.346 1.32 -2.722c-.203 -1.297 -1.047 -2.267 -1.932 -2.267c-.055 0 -.109 0 -.163 .011c-.944 .128 -1.533 1.346 -1.32 2.722c.204 1.293 1.048 2.267 1.933 2.267z" />
    <path d="M16.456 6.733c.214 -1.376 -.375 -2.594 -1.32 -2.722a1.164 1.164 0 0 0 -.162 -.011c-.885 0 -1.728 .97 -1.93 2.267c-.214 1.376 .375 2.594 1.32 2.722c.054 .007 .108 .011 .162 .011c.885 0 1.73 -.974 1.93 -2.267z" />
    <path d="M5.69 12.918c.816 -.352 1.054 -1.719 .536 -3.052c-.436 -1.124 -1.271 -1.866 -2.009 -1.866c-.14 0 -.277 .027 -.407 .082c-.816 .352 -1.054 1.719 -.536 3.052c.436 1.124 1.271 1.866 2.009 1.866c.14 0 .277 -.027 .407 -.082z" />
  </svg>
);

const DogIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="icon icon-tabler icons-tabler-outline icon-tabler-dog"
  >
    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
    <path d="M11 5h2" />
    <path d="M19 12c-.667 5.333 -2.333 8 -5 8h-4c-2.667 0 -4.333 -2.667 -5 -8" />
    <path d="M11 16c0 .667 .333 1 1 1s1 -.333 1 -1h-2z" />
    <path d="M12 18v2" />
    <path d="M10 11v.01" />
    <path d="M14 11v.01" />
    <path d="M5 4l6 .97l-6.238 6.688a1.021 1.021 0 0 1 -1.41 .111a.953 .953 0 0 1 -.327 -.954l1.975 -6.815z" />
    <path d="M19 4l-6 .97l6.238 6.688c.358 .408 .989 .458 1.41 .111a.953 .953 0 0 0 .327 -.954l-1.975 -6.815z" />
  </svg>
);

const HorseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="icon icon-tabler icons-tabler-outline icon-tabler-horse"
  >
    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
    <path d="M7 10l-.85 8.507a1.357 1.357 0 0 0 1.35 1.493h.146a2 2 0 0 0 1.857 -1.257l.994 -2.486a2 2 0 0 1 1.857 -1.257h1.292a2 2 0 0 1 1.857 1.257l.994 2.486a2 2 0 0 0 1.857 1.257h.146a1.37 1.37 0 0 0 1.364 -1.494l-.864 -9.506h-8c0 -3 -3 -5 -6 -5l-3 6l2 2l3 -2z" />
    <path d="M22 14v-2a3 3 0 0 0 -3 -3" />
  </svg>
);

// Phone configuration by country
const countryPhoneConfigs = [
  { code: '+1', country: 'USA', flag: '🇺🇸', maxDigits: 10, format: '(XXX) XXX-XXXX' },
  { code: '+44', country: 'UK', flag: '🇬🇧', maxDigits: 10, format: 'XXXX XXXXXX' },
  { code: '+61', country: 'AUS', flag: '🇦🇺', maxDigits: 9, format: 'XXX XXX XXX' },
  { code: '+91', country: 'IND', flag: '🇮🇳', maxDigits: 10, format: 'XXXXX XXXXX' },
  { code: '+86', country: 'CHN', flag: '🇨🇳', maxDigits: 11, format: 'XXX XXXX XXXX' },
  { code: '+81', country: 'JPN', flag: '🇯🇵', maxDigits: 10, format: 'XX XXXX XXXX' },
  { code: '+49', country: 'GER', flag: '🇩🇪', maxDigits: 11, format: 'XXX XXXXXXXX' },
  { code: '+33', country: 'FRA', flag: '🇫🇷', maxDigits: 9, format: 'X XX XX XX XX' },
  { code: '+39', country: 'ITA', flag: '🇮🇹', maxDigits: 10, format: 'XXX XXXXXXX' },
  { code: '+34', country: 'ESP', flag: '🇪🇸', maxDigits: 9, format: 'XXX XXX XXX' },
  { code: '+7', country: 'RUS', flag: '🇷🇺', maxDigits: 10, format: 'XXX XXX-XX-XX' },
  { code: '+55', country: 'BRA', flag: '🇧🇷', maxDigits: 11, format: 'XX XXXXX-XXXX' },
  { code: '+52', country: 'MEX', flag: '🇲🇽', maxDigits: 10, format: 'XX XXXX XXXX' },
  { code: '+82', country: 'KOR', flag: '🇰🇷', maxDigits: 10, format: 'XX XXXX XXXX' },
  { code: '+1-CA', country: 'CAN', flag: '🇨🇦', maxDigits: 10, format: '(XXX) XXX-XXXX' }
];

// Initial validation errors state - extracted to avoid duplication
const INITIAL_VALIDATION_ERRORS = {
  property: '',
  unit: '',
  contactEmail: '',
  photos: '',
  petType: '',
  breed: '',
  age: '',
  weight: '',
  gender: '',
  spayed_neutered: null,
  color: '',
  move_in_date: '',
  move_out_date: '',
  markings: '',
  previousPetOwnership: '',
  vetClinic: '',
  vetAddress: '',
  esaProfessional: '',
  esaLetterDate: ''
};

const EMPTY_DOCS = [];

const QuestionnaireModal = ({
  isOpen,
  onClose,
  onComplete,
  onPetComplete,
  isMultiPetWorkflow = false,
  currentPetIndex = 0,
  petStartToken = 0,
  userProperty = null,
  draftPet = null,
  onDraftSaved = null,
  // Snapshot of the per-lease cap from /api/pets/lease-summary, threaded
  // down by ClientDashboard so the pre-flight check can compare against
  // pets already on the lease (counts co-residents' pets too). Each
  // questionnaire run registers one pet; multi-pet batch size is ignored.
  leasePetSummary = null,
  dashboardDraftCount = 0,
}) => {
  const [step, setStep] = useState(1);
  const [properties, setProperties] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [isStep1Loading, setIsStep1Loading] = useState(false);

  // Questionnaire settings - fetched from properties.extra_data when property is selected
  const [questionnaireSettings, setQuestionnaireSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);

  // Phone country-code state. Phone is an optional field with no
  // validation — partial entries are accepted, no error states are
  // rendered. The maxLength cap on the input still prevents
  // pathologically long values.
  const [selectedCountryCode, setSelectedCountryCode] = useState('+1');
  const [selectedVetCountryCode, setSelectedVetCountryCode] = useState('+1');
  const [vetPhoneValidationError, setVetPhoneValidationError] = useState('');
  const [petNameValidationError, setPetNameValidationError] = useState('');
  
  // Pet type autocomplete states
  const [petTypeSuggestions, setPetTypeSuggestions] = useState<string[]>([]);
  const [showPetTypeSuggestions, setShowPetTypeSuggestions] = useState(false);

  // Address autocomplete states
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [addressSearchLoading, setAddressSearchLoading] = useState(false);
  const isSelectingAddress = useRef(false);

  // Get Covered resident info states
  const [gcResidentInfo, setGCResidentInfo] = useState<ResidentInfo | null>(null);
  const [isLoadingGCData, setIsLoadingGCData] = useState(false);
  const [gcDataError, setGCDataError] = useState<string | null>(null);

  // Snapshot of unit/phone values present on the lease at the moment we
  // opened the modal — sourced from users.{unit, contact_phone} (which is
  // GC's value when GC has it, resident's saved value when not). Used to
  // decide read-only-vs-editable so the rendering doesn't flip while the
  // resident is mid-type (formData changes on every keystroke; this doesn't).
  const [prefilledLeaseValues, setPrefilledLeaseValues] = useState<{ unit: string; phone: string }>({ unit: '', phone: '' });
  
  // Photo preview modal state
  const [showPhotoPreview, setShowPhotoPreview] = useState(false);

  // Document upload states
  const initialUploadStates = {
    petInsurance: { status: 'idle', file: null },
    vaccination: { status: 'idle', file: null },
    lastVisit: ''
  };
  const [uploadStates, setUploadStates] = useState(initialUploadStates);
  const [petDocuments, setPetDocuments] = useState({});
  // Bumps whenever the resident leaves a draft so an in-flight document fetch
  // for the previous pet cannot write into the next one.
  const documentsRequestRef = useRef(0);

  // Step 6 (Review & Attestation): checkbox starts unchecked; submit disabled until acknowledged
  const [attestationAcknowledged, setAttestationAcknowledged] = useState(false);
  // Draft pet id for upload-as-you-go (synced from prop, then updated when we auto-create)
  const [activeDraftId, setActiveDraftId] = useState(
    () => draftPet?.id || draftPet?.pet_id || null,
  );
  const [photoUploadStatus, setPhotoUploadStatus] = useState('idle'); // idle|uploading|success|failed
  const [uploadBlockMessage, setUploadBlockMessage] = useState('');

  // Close confirmation modal state
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  // Reload confirmation modal state
  const [showReloadConfirm, setShowReloadConfirm] = useState(false);

  useEffect(() => {
    if (!isMultiPetWorkflow) return;
    setUploadStates(initialUploadStates);
    const key = `pet_${currentPetIndex}`;
    setPetDocuments(prev => (prev[key] ? prev : { ...prev, [key]: [] }));
  }, [isMultiPetWorkflow, currentPetIndex, isOpen]);
  
  // Comprehensive validation errors state
  const [validationErrors, setValidationErrors] = useState({...INITIAL_VALIDATION_ERRORS});
  const [customFieldErrors, setCustomFieldErrors] = useState<Record<string, string>>({});

  // Scroll container for the active step. Used to scope the "scroll to first
  // invalid field" behaviour to the step the user is actually on (only the
  // current step is rendered, but this keeps the query robust either way).
  const stepScrollRef = useRef<HTMLDivElement>(null);

  // When a Next-click fails validation, bring the first invalid field into view
  // and focus it. Every validation error message renders with `animate-pulse`
  // (a dedicated marker used nowhere else), so the first one in DOM order is the
  // topmost error. We scroll its field block to center and focus the nearest
  // editable control so the resident immediately sees — and can fix — the
  // problem instead of clicking Next into a silent no-op.
  const scrollToFirstInvalidField = () => {
    requestAnimationFrame(() => {
      const root: ParentNode = stepScrollRef.current || document;
      const firstError = root.querySelector('.animate-pulse');
      if (!firstError) return;

      const fieldBlock = (firstError.parentElement as HTMLElement) || (firstError as HTMLElement);
      fieldBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Focus a visible, editable control within the field (skips hidden/file
      // inputs like the photo picker). Delayed so the smooth scroll isn't
      // interrupted on iOS Safari.
      const focusable = fieldBlock.querySelector(
        'input:not([type="hidden"]):not([type="file"]), select, textarea'
      ) as HTMLElement | null;
      if (focusable) {
        setTimeout(() => {
          try { focusable.focus({ preventScroll: true }); } catch { focusable.focus(); }
        }, 350);
      }
    });
  };

  // Helper to create a fresh pet object
  const initialPet = () => ({
    petName: '',
    petType: 'dog',
    customPetType: '',
    breed: '',
    customBreed: '',
    age: null,
    weight: {
      value: '',
      unit: 'lbs'
    },
    color: '',
    customColor: '',
    markings: '',
    customMarkings: '',
    gender: '',  // Male, Female, Other
    move_in_date: '',  // ISO date string
    move_out_date: '',  // ISO date string
    spayed_neutered: null,  // true/false/null
    previousPetOwnership: '',  // Yes/No - only for regular pets
    animalType: 'pet',  // pet, esa, or sa
    isESA: false,
    vetClinic: '',
    vetPhone: '',
    vetPhoneCountryCode: '+1',
    vetPhoneNumber: '',
    vetAddress: '',
    lastVisit: '',
    esaProfessional: '',
    esaLetterDate: '',
    esaProviderLicenseNumber: '',
    esaProviderLicenseState: '',
    saRequiredForDisability: '',
    saTasksTrained: '',
    documents: [],
    photos: [
      { file: null, preview: null, type: 'main', label: 'Pet Photo' }
    ],
    customFields: {} as Record<string, string>,
  });

  const [formData, setFormData] = useState({
    property: '',
    unit: '',
    contactEmail: '',
    contactPhone: '',
    phoneCountryCode: '+1',
    phoneNumber: '',
    pets: [initialPet()]
  });

  // Pre-fill property from user's assigned property
  useEffect(() => {
    if (userProperty && isOpen && !gcResidentInfo) {
      setFormData(prev => ({
        ...prev,
        property: userProperty.name || ''
      }));
    }
  }, [userProperty, isOpen, gcResidentInfo]);

  // Helper to extract YYYY-MM-DD from various datetime string formats
  const parseDateToYMD = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : '';
  };

  // Helper to create a new pet with pre-populated dates from resident info
  const createPetWithResidentDates = () => {
    const pet = initialPet();
    if (gcResidentInfo?.move_in) {
      pet.move_in_date = parseDateToYMD(gcResidentInfo.move_in);
    }
    if (gcResidentInfo?.expected_move_out) {
      pet.move_out_date = parseDateToYMD(gcResidentInfo.expected_move_out);
    }
    return pet;
  };

  useEffect(() => {
    if (!isMultiPetWorkflow) return;
    setFormData(prev => {
      const pets = [...prev.pets];
      while (pets.length <= currentPetIndex) {
        pets.push(createPetWithResidentDates());
      }
      return { ...prev, pets };
    });
  }, [isMultiPetWorkflow, currentPetIndex]);

  useEffect(() => {
    if (!isMultiPetWorkflow || !isOpen) return;
    setStep(1);
    setValidationErrors({...INITIAL_VALIDATION_ERRORS});
    setPetNameValidationError('');
    setVetPhoneValidationError('');
    setAttestationAcknowledged(false);
    setFormData(prev => {
      const pets = [...prev.pets];
      while (pets.length <= currentPetIndex) {
        pets.push(createPetWithResidentDates());
      }
      pets[currentPetIndex] = createPetWithResidentDates();
      return { ...prev, pets };
    });
    setUploadStates(initialUploadStates);
    const key = `pet_${currentPetIndex}`;
    setPetDocuments(prev => ({ ...prev, [key]: [] }));
  }, [petStartToken, isMultiPetWorkflow, currentPetIndex, isOpen]);

  // Cleanup blob URLs when modal closes
  useEffect(() => {
    return () => {
      // Only revoke URLs when modal is actually closing (isOpen becomes false)
      if (!isOpen && formData.pets) {
        formData.pets.forEach(pet => {
          pet.photos?.forEach(photo => {
            if (photo.preview && photo.preview.startsWith('blob:')) {
              URL.revokeObjectURL(photo.preview);
            }
          });
        });
      }
    };
  }, [isOpen, formData.pets]);

  // Check if user has started filling out the form
  const hasStartedForm = useCallback(() => {
    const currentPet = formData.pets?.[currentPetIndex] || formData.pets?.[0];
    if (!currentPet) return false;

    // Check basic form fields
    if (formData.unit?.trim()) return true;
    if (formData.contactEmail?.trim()) return true;
    if (formData.contactPhone?.trim()) return true;

    // Check pet-specific fields
    if (currentPet.petName?.trim()) return true;
    if (currentPet.breed?.trim()) return true;
    if (currentPet.age) return true;
    if (currentPet.weight?.value) return true;
    if (currentPet.color?.trim()) return true;
    if (currentPet.markings?.trim()) return true;
    if (currentPet.gender) return true;
    if (currentPet.vetClinic?.trim()) return true;
    if (currentPet.vetPhone?.trim()) return true;
    if (currentPet.vetAddress?.trim()) return true;

    // Check if any photos have been uploaded
    if (currentPet.photos?.some(p => p.file !== null)) return true;

    // Check if user has progressed past step 1
    if (step > 1) return true;

    return false;
  }, [formData, currentPetIndex, step]);

  // Reset form to initial state
  const resetForm = useCallback(() => {
    setStep(1);
    setFormData({
      property: userProperty?.name || '',
      unit: '',
      contactEmail: '',
      contactPhone: '',
      phoneCountryCode: '+1',
      phoneNumber: '',
      pets: [initialPet()]
    });
    setAttestationAcknowledged(false);
    setUploadStates({
      petInsurance: { status: 'idle', file: null },
      vaccination: { status: 'idle', file: null },
      lastVisit: ''
    });
    setPetDocuments({});
    setGCResidentInfo(null);
    setPrefilledLeaseValues({ unit: '', phone: '' });
    setValidationErrors({...INITIAL_VALIDATION_ERRORS});
  }, [userProperty]);

  // Initialize form from draft pet data when resuming a draft
  useEffect(() => {
    if (!isOpen || !draftPet) return;

    const dp = draftPet;
    const phoneParsed = parsePhoneNumber(dp.contact_phone || dp.contactPhone);
    const vetPhoneParsed = parsePhoneNumber(dp.vet_phone || dp.vetPhone);

    const existingImageUrl = dp.image_url || null;
    if (existingImageUrl) {
      setPhotoUploadStatus('success');
    }

    setFormData({
      property: dp.property || '',
      unit: dp.unit || '',
      contactEmail: dp.contact_email || dp.contactEmail || '',
      contactPhone: dp.contact_phone || dp.contactPhone || '',
      phoneCountryCode: phoneParsed.countryCode,
      phoneNumber: phoneParsed.number,
      pets: [{
        ...initialPet(),
        ...(existingImageUrl
          ? {
              photos: [
                {
                  file: null,
                  preview: existingImageUrl,
                  type: 'main',
                  label: 'Pet Photo',
                  uploadStatus: 'success',
                },
              ],
              photoUploadStatus: 'success',
            }
          : {}),
        petName: dp.pet_name || dp.petName || '',
        petType: (() => {
          const pt = dp.pet_type || dp.petType || 'dog';
          return ['dog', 'cat'].includes(pt) ? pt : 'other';
        })(),
        customPetType: (() => {
          const pt = dp.pet_type || dp.petType || 'dog';
          return ['dog', 'cat'].includes(pt) ? '' : (pt === 'other' ? '' : pt);
        })(),
        breed: (() => {
          const savedBreed = dp.breed || '';
          if (!savedBreed) return '';
          const pt = dp.pet_type || dp.petType || 'dog';
          if (!['dog', 'cat'].includes(pt)) return savedBreed;
          const knownBreeds = pt === 'dog' ? dogBreeds : catBreeds;
          return knownBreeds.includes(savedBreed) ? savedBreed : 'Other';
        })(),
        customBreed: (() => {
          const savedBreed = dp.breed || '';
          if (!savedBreed) return '';
          const pt = dp.pet_type || dp.petType || 'dog';
          if (!['dog', 'cat'].includes(pt)) return '';
          const knownBreeds = pt === 'dog' ? dogBreeds : catBreeds;
          return knownBreeds.includes(savedBreed) ? '' : savedBreed;
        })(),
        age: typeof dp.age === 'number' ? dp.age : null,
        weight: { value: dp.weight || '', unit: 'lbs' },
        color: (() => {
          const saved = dp.color || '';
          if (!saved) return '';
          return petColors.includes(saved) ? saved : 'Other';
        })(),
        customColor: (() => {
          const saved = dp.color || '';
          if (!saved) return '';
          return petColors.includes(saved) ? '' : saved;
        })(),
        markings: (() => {
          const saved = dp.markings || '';
          if (!saved) return '';
          return petMarkings.includes(saved) ? saved : 'Other';
        })(),
        customMarkings: (() => {
          const saved = dp.markings || '';
          if (!saved) return '';
          return petMarkings.includes(saved) ? '' : saved;
        })(),
        gender: dp.gender || '',
        move_in_date: dp.move_in_date || '',
        move_out_date: dp.move_out_date || '',
        spayed_neutered: dp.spayed_neutered ?? null,
        previousPetOwnership:
          dp.previous_pet_ownership === true
            ? 'Yes'
            : dp.previous_pet_ownership === false
              ? 'No'
              : (dp.previousPetOwnership || ''),
        animalType: dp.pet_role || dp.animalType || 'pet',
        isESA: (dp.pet_role || dp.animalType) === 'esa',
        vetClinic: dp.vet_clinic || dp.vetClinic || '',
        vetPhone: dp.vet_phone || dp.vetPhone || '',
        vetPhoneCountryCode: vetPhoneParsed.countryCode,
        vetPhoneNumber: vetPhoneParsed.number,
        vetAddress: dp.vet_address || dp.vetAddress || '',
        lastVisit: dp.last_visit || dp.lastVisit || '',
        esaProfessional: dp.esa_professional || dp.esaProfessional || '',
        esaLetterDate: dp.esa_letter_date || dp.esaLetterDate || '',
        esaProviderLicenseNumber: dp.esa_registration || dp.esaRegistration || dp.esaProviderLicenseNumber || '',
        saRequiredForDisability: dp.sa_required_for_disability || dp.saRequiredForDisability || '',
        saTasksTrained: dp.sa_tasks_trained || dp.saTasksTrained || '',
        customFields: dp.custom_fields || dp.customFields || {},
      }],
    });

    setSelectedCountryCode(phoneParsed.countryCode);
    setSelectedVetCountryCode(vetPhoneParsed.countryCode);

    // Use saved draft_step when available for precise resume
    const savedStep = dp.draft_step;
    if (savedStep && savedStep >= 1 && savedStep <= totalSteps) {
      setStep(savedStep);
    } else {
      // Fallback: infer step from field data (for older drafts without draft_step)
      const hasVet = (dp.vet_clinic || dp.vetClinic || '').trim();
      const hasPhoto = !!dp.image_url;
      const hasPetName = (dp.pet_name || dp.petName || '').trim();
      const hasBreed = (dp.breed || '').trim();
      const hasAge = typeof dp.age === 'number' && dp.age > 0;
      const hasWeight = dp.weight && Number(dp.weight) > 0;
      const hasColor = (dp.color || '').trim();
      const hasGender = (dp.gender || '').trim();
      const hasPetDetails = !!(hasPetName || hasBreed || hasAge || hasWeight || hasColor || hasGender);
      const petRole = (dp.pet_role || dp.animalType || 'pet').toLowerCase();
      const hasExplicitAnimalType = petRole === 'esa' || petRole === 'sa';
      const hasProp = (dp.property || '').trim();
      const hasUnit = (dp.unit || '').trim();
      const hasEmail = (dp.contact_email || dp.contactEmail || '').trim();
      const hasPhone = (dp.contact_phone || dp.contactPhone || '').trim();
      const step1Complete = !!(hasProp && hasUnit && hasEmail && hasPhone);

      if (hasVet) setStep(5);
      else if (hasPetDetails || hasPhoto) setStep(3);
      else if (hasExplicitAnimalType || step1Complete) setStep(2);
      else setStep(1);
    }

    // Drop whatever the previous pet left in memory before this draft's
    // documents arrive. An empty result is real: this pet has not uploaded yet.
    const requestId = ++documentsRequestRef.current;
    setPetDocuments(prev => ({ ...prev, pet_0: [] }));

    const draftId = dp.id || dp.pet_id;
    if (draftId) {
      (async () => {
        try {
          const docs = await petService.getPetDocuments(draftId);
          const restoredDocs = (docs || []).map((doc: any) => ({
            id: `restored_${doc.id}`,
            file: null,
            name: doc.original_filename || doc.display_name || doc.name || 'Document',
            size: doc.size || 0,
            type: doc.document_type || doc.type || 'other',
            mimeType: doc.mime_type || 'application/octet-stream',
            status: 'ready' as const,
            docType: doc.document_type || doc.type || 'vaccination',
            previewData: doc.s3_url || null,
            fileData: null,
            addedAt: new Date(doc.uploaded_at || Date.now()),
            uploadedAt: doc.uploaded_at || new Date().toISOString(),
            petName: dp.pet_name || dp.petName || '',
            petIndex: 0,
            petNumber: 1,
            s3_url: doc.s3_url || null,
            s3_key: doc.s3_key || null,
            restoredFromServer: true,
          }));
          const restored = restoredDraftDocuments(
            restoredDocs,
            documentsRequestRef.current === requestId,
          );
          if (restored === null) return;
          setPetDocuments(prev => ({
            ...prev,
            pet_0: mergeRestoredDraftDocuments(restored, prev.pet_0),
          }));
          console.log(`📄 Restored ${restored.length} document(s) from draft`);
        } catch (err) {
          console.warn('Could not restore documents from draft:', err);
        }
      })();
    }

    return () => {
      if (documentsRequestRef.current === requestId) {
        documentsRequestRef.current += 1;
      }
    };
  }, [isOpen, draftPet]);

  const buildDraftPayload = useCallback(() => {
    const currentPet = formData.pets?.[currentPetIndex] || formData.pets?.[0];
    return {
      property_info: {
        property: formData.property || '',
        unit: formData.unit || '',
        contact_email: formData.contactEmail || '',
        contact_phone: formData.contactPhone || '',
      },
      pet_details: {
        pet_name: currentPet?.petName || '',
        pet_type: currentPet?.petType === 'other' ? (currentPet?.customPetType || 'other') : (currentPet?.petType || 'dog'),
        breed: currentPet?.breed === 'Other' ? (currentPet?.customBreed || 'Other') : (currentPet?.breed || ''),
        age: currentPet?.age || 0,
        weight: typeof currentPet?.weight === 'object' ? (currentPet.weight.value || 0) : (parseFloat(String(currentPet?.weight)) || 0),
        color: currentPet?.color === 'Other' ? (currentPet?.customColor || 'Other') : (currentPet?.color || null),
        markings: currentPet?.markings === 'Other' ? (currentPet?.customMarkings || 'Other') : (currentPet?.markings || null),
        gender: currentPet?.gender || null,
        move_in_date: currentPet?.move_in_date || null,
        move_out_date: currentPet?.move_out_date || null,
        spayed_neutered: currentPet?.spayed_neutered,
        previous_pet_ownership:
          currentPet?.previousPetOwnership === 'Yes'
            ? true
            : currentPet?.previousPetOwnership === 'No'
              ? false
              : null,
      },
      veterinary_info: {
        vet_clinic: currentPet?.vetClinic || '',
        vet_phone: currentPet?.vetPhone || '',
        vet_address: currentPet?.vetAddress || null,
        last_visit: currentPet?.lastVisit || null,
      },
      esa_info: {
        pet_role: currentPet?.animalType || 'pet',
        esa_professional: currentPet?.esaProfessional || null,
        esa_letter_date: currentPet?.esaLetterDate || null,
        esa_registration: currentPet?.esaProviderLicenseNumber || currentPet?.esaRegistration || null,
      },
      custom_fields: currentPet?.customFields || {},
      draft_step: step,
    };
  }, [formData, currentPetIndex, step]);

  // Reset upload-as-you-go state when the modal closes; re-seed from draftPet when
  // opening (resume) or clear for a fresh registration (Add Pet).
  useEffect(() => {
    if (!isOpen) {
      documentsRequestRef.current += 1;
      setPetDocuments({});
      setActiveDraftId(null);
      setPhotoUploadStatus('idle');
      setUploadBlockMessage('');
      return;
    }
    setActiveDraftId(draftPet?.id || draftPet?.pet_id || null);
  }, [isOpen, draftPet?.id, draftPet?.pet_id]);

  const ensureDraftPet = useCallback(async () => {
    if (activeDraftId) {
      try {
        const existing = await petService.getPetDetails(Number(activeDraftId));
        if ((existing.status || '').toLowerCase() === 'drafted') {
          return Number(activeDraftId);
        }
        console.warn(
          'Stale draft id %s (status=%s) — creating a new draft',
          activeDraftId,
          existing.status,
        );
        setActiveDraftId(null);
      } catch (err) {
        console.warn('Could not verify draft pet — creating a new draft:', err);
        setActiveDraftId(null);
      }
    }
    const payload = buildDraftPayload();
    const result = await petService.saveDraft(payload);
    const newId = result?.id;
    if (newId) {
      setActiveDraftId(newId);
      if (onDraftSaved) onDraftSaved(newId);
    }
    return newId ? Number(newId) : null;
  }, [activeDraftId, buildDraftPayload, onDraftSaved]);

  const handleSaveDraft = useCallback(async () => {
    setIsSavingDraft(true);
    try {
      const payload = buildDraftPayload();
      const draftId = activeDraftId || draftPet?.id || draftPet?.pet_id;

      let savedPetId = draftId;
      if (draftId) {
        await petService.updateDraft(draftId, payload);
      } else {
        const result = await petService.saveDraft(payload);
        savedPetId = result?.id;
        if (savedPetId) setActiveDraftId(savedPetId);
      }

      // Upload any remaining local files (best-effort; prefer upload-on-select path)
      if (savedPetId) {
        const currentPet = formData.pets?.[currentPetIndex] || formData.pets?.[0];
        const photoFiles = currentPet?.photos
          ?.filter((p: any) => p.file && p.uploadStatus !== 'success')
          .map((p: any) => p.file) || [];
        for (const photoFile of photoFiles) {
          try {
            await uploadFileToPet({
              petId: Number(savedPetId),
              file: photoFile,
              uploadType: 'pet_photo',
            });
          } catch (photoErr) {
            console.warn('Photo upload during draft save failed:', photoErr);
          }
        }

        const allDocs = Object.values(petDocuments).flat();
        for (const doc of allDocs) {
          if (doc.restoredFromServer || doc.uploadStatus === 'success') continue;
          if (doc.file) {
            try {
              await uploadFileToPet({
                petId: Number(savedPetId),
                file: doc.file,
                uploadType: 'document',
                documentType: doc.docType || null,
                documentName: doc.name || null,
              });
            } catch (docErr) {
              console.warn('Document upload during draft save failed:', docErr);
            }
          }
        }
      }

      setShowConfirmClose(false);
      resetForm();
      onClose();
      if (onDraftSaved) onDraftSaved(savedPetId);
    } catch (err) {
      console.error('Failed to save draft:', err);
    } finally {
      setIsSavingDraft(false);
    }
  }, [buildDraftPayload, draftPet, activeDraftId, formData, currentPetIndex, petDocuments, resetForm, onClose, onDraftSaved]);

  // Intercept refresh shortcuts (F5, Ctrl+R/Cmd+R) to show a custom confirmation
  // modal instead of auto-saving a draft. Also keep beforeunload as a fallback
  // for browser-button refreshes and tab/window closes.
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!hasStartedForm()) return;
      const isRefresh =
        e.key === 'F5' ||
        ((e.ctrlKey || e.metaKey) && e.key === 'r');
      if (isRefresh) {
        e.preventDefault();
        setShowReloadConfirm(true);
      }
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!hasStartedForm()) return;
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isOpen, hasStartedForm]);

  // Handle close attempt with confirmation
  const handleCloseAttempt = useCallback(() => {
    if (hasStartedForm()) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  }, [hasStartedForm, onClose]);

  // Handle confirmed cancellation - delete backend draft, refresh dashboard, then close
  const handleConfirmCancel = useCallback(async () => {
    const draftId = activeDraftId || draftPet?.id || draftPet?.pet_id;
    if (draftId) {
      setIsDiscarding(true);
      try {
        await petService.deletePet(Number(draftId));
        if (onDraftSaved) await onDraftSaved();
      } catch (err) {
        console.error('Failed to delete draft on discard:', err);
      } finally {
        setIsDiscarding(false);
      }
    }
    setShowConfirmClose(false);
    resetForm();
    onClose();
  }, [draftPet, activeDraftId, resetForm, onClose, onDraftSaved]);

  // Handle reload with draft save
  const handleReloadWithSave = useCallback(async () => {
    setIsSavingDraft(true);
    try {
      const payload = buildDraftPayload();
      const draftId = draftPet?.id || draftPet?.pet_id;
      let savedPetId = draftId;
      if (draftId) {
        await petService.updateDraft(draftId, payload);
      } else {
        const result = await petService.saveDraft(payload);
        savedPetId = result?.id;
      }

      // Upload pending documents before reload so they aren't lost
      if (savedPetId) {
        const allDocs = Object.values(petDocuments).flat();
        for (const doc of allDocs) {
          if (doc.restoredFromServer) continue;
          if (doc.file) {
            try {
              await petService.uploadPetDocuments(savedPetId, [doc.file], doc.docType || null, doc.name || null);
            } catch (_) { /* best-effort */ }
          }
        }
      }
    } catch (err) {
      console.error('Failed to save draft before reload:', err);
    } finally {
      setIsSavingDraft(false);
      setShowReloadConfirm(false);
      window.location.reload();
    }
  }, [buildDraftPayload, draftPet, petDocuments]);

  // Handle reload without saving
  const handleReloadWithoutSave = useCallback(() => {
    setShowReloadConfirm(false);
    window.location.reload();
  }, []);

  // ESC key handler
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleCloseAttempt();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [isOpen, handleCloseAttempt]);

  // Address autocomplete functionality
  const searchAddress = async (query) => {
    if (!query || query.trim().length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }

    setAddressSearchLoading(true);
    
    try {
      const url = `https://addresses-dev.getcoveredinsurance.com/lookup?q=${encodeURIComponent(query)}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      const suggestions = (data.data || []).map(addr => ({
        source: addr.source,
        display: addr.full_str,
        full: addr.full_str,
        data: addr
      }));
      
      setAddressSuggestions(suggestions);
      setShowAddressSuggestions(suggestions.length > 0);
      
    } catch (error) {
      console.error('Address API error:', error.message);
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
    } finally {
      setAddressSearchLoading(false);
    }
  };

  const handleAddressSelection = (suggestion) => {
    isSelectingAddress.current = true;

    handleInputChange('pet.vetAddress', suggestion.full);
    setShowAddressSuggestions(false);
    setAddressSuggestions([]);

     // Reset flag after a short delay to allow the effect to skip
    setTimeout(() => {
      isSelectingAddress.current = false;
    }, 300);
  };

  // Debounce address search
  useEffect(() => {
    // Skip if we're programmatically setting the address from selection
    if (isSelectingAddress.current) {
      return;
    }

    const currentPet = formData.pets[currentPetIndex] || formData.pets[0];
    
    const timer = setTimeout(() => {
      if (currentPet?.vetAddress) {
        searchAddress(currentPet.vetAddress);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [formData.pets[currentPetIndex]?.vetAddress]);

  // Close address suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAddressSuggestions && !event.target.closest('.address-autocomplete-container')) {
        setShowAddressSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAddressSuggestions]);

  // Comprehensive list of common "Other" pet types for autocomplete suggestions
  const otherPetTypes = [
    'Alpaca', 'Axolotl', 'Bearded Dragon', 'Bird', 'Canary', 'Chameleon',
    'Chicken', 'Chinchilla', 'Cockatiel', 'Cockatoo', 'Crab', 'Degu',
    'Dove', 'Duck', 'Ferret', 'Finch', 'Fish', 'Frog', 'Gecko',
    'Gerbil', 'Goat', 'Goldfish', 'Guinea Pig', 'Hamster', 'Hedgehog',
    'Hermit Crab', 'Horse', 'Iguana', 'Lizard', 'Lovebird', 'Macaw',
    'Miniature Horse', 'Miniature Pig', 'Mouse', 'Newt', 'Parakeet',
    'Parrot', 'Pigeon', 'Pot-bellied Pig', 'Rabbit', 'Rat', 'Salamander',
    'Scorpion', 'Sheep', 'Shrimp', 'Snake', 'Spider', 'Sugar Glider',
    'Tarantula', 'Toad', 'Tortoise', 'Turtle'
  ];

  const isSelectingPetType = useRef(false);
  const petPhotoInputRef = useRef<HTMLInputElement>(null);
  const openPetPhotoPicker = () => petPhotoInputRef.current?.click();

  const handlePetTypeSearch = (query: string) => {
    handleInputChange('pet.customPetType', query);
    if (!query.trim()) {
      setPetTypeSuggestions([]);
      setShowPetTypeSuggestions(false);
      return;
    }
    const lower = query.toLowerCase();
    const filtered = otherPetTypes.filter(t => t.toLowerCase().includes(lower));
    setPetTypeSuggestions(filtered);
    setShowPetTypeSuggestions(filtered.length > 0);
  };

  const handlePetTypeSelection = (type: string) => {
    isSelectingPetType.current = true;
    handleInputChange('pet.customPetType', type);
    setPetTypeSuggestions([]);
    setShowPetTypeSuggestions(false);
    setTimeout(() => { isSelectingPetType.current = false; }, 300);
  };

  // Close pet type suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPetTypeSuggestions && !event.target.closest('.pet-type-autocomplete-container')) {
        setShowPetTypeSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPetTypeSuggestions]);

  const dogBreeds = [
    'Akita',
    'Alaskan Malamute',
    'American Staffordshire Terrier',
    'Anatolian Shepherd',
    'Australian Shepherd',
    'Basset Hound',
    'Beagle',
    'Belgian Malinois',
    'Belgian Shepherd',
    'Bernese Mountain Dog',
    'Bichon Frise',
    'Boston Terrier',
    'Boxer',
    'Briard',
    'Brittany Spaniel',
    'Bulldog',
    'Cane Corso',
    'Cavalier King Charles Spaniel',
    'Chihuahua',
    'Collie',
    'Dachshund',
    'Doberman Pinscher',
    'English Cocker Spaniel',
    'English Springer Spaniel',
    'French Bulldog',
    'German Shepherd',
    'German Shorthaired Pointer',
    'Golden Retriever',
    'Great Dane',
    'Havanese',
    'Labrador Retriever',
    'Maltese',
    'Miniature Schnauzer',
    'Newfoundland',
    'Papillon',
    'Pembroke Welsh Corgi',
    'Pit Bull',
    'Pomeranian',
    'Poodle',
    'Pug',
    'Rhodesian Ridgeback',
    'Rottweiler',
    'Shetland Sheepdog',
    'Shih Tzu',
    'Siberian Husky',
    'Staffordshire Bull Terrier',
    'Weimaraner',
    'West Highland White Terrier',
    'Yorkshire Terrier',
    'Other'
  ];

  const catBreeds = [
    'Abyssinian', 'American Shorthair', 'Bengal', 'Birman', 'British Shorthair', 'Burmese',
    'Devon Rex', 'Domestic Shorthair', 'Exotic Shorthair', 'Himalayan', 'Maine Coon', 'Manx',
    'Munchkin', 'Norwegian Forest Cat', 'Oriental Shorthair', 'Persian', 'Ragdoll', 'Russian Blue',
    'Savannah', 'Scottish Fold', 'Siamese', 'Sphynx', 'Tonkinese', 'Turkish Angora', 'Mixed Breed', 'Other'
  ];

  const miniatureHorseBreeds = [
    'American Miniature Horse', 'Falabella', 'Miniature Shetland Pony', 'Noma',
    'South African Miniature', 'Yonaguni', 'Other'
  ];

  const petColors = [
    'Apricot', 'Beige', 'Black', 'Blue', 'Blue Merle', 'Brindle', 'Brown', 'Chocolate', 'Cream',
    'Fawn', 'Golden', 'Gray', 'Grey', 'Isabella', 'Liver', 'Mahogany', 'Orange', 'Red', 'Red Merle',
    'Rust', 'Sable', 'Silver', 'Tan', 'Tri-color', 'White', 'Yellow', 'Other'
  ];

  const petMarkings = [
    'Blaze (white stripe on face)', 'Boston markings (white chest/face)', 'Collie markings (white collar/chest/legs)',
    'Irish markings (white chest/feet/tip of tail)', 'Mantle (dark with white chest/legs)', 'Mask (dark face)',
    'Merle pattern', 'No markings/Solid color', 'Parti-color (large patches of color)', 'Patches',
    'Piebald (irregular patches)', 'Points (darker ears/muzzle/legs)', 'Roan (mixed colored and white hairs)',
    'Sable pattern', 'Spots', 'Stripes', 'Ticking (small spots throughout coat)', 'Tipped ears',
    'Tuxedo (black with white chest/paws)', 'White chest', 'White feet/socks', 'White markings', 'White patches', 'Other'
  ];

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getOneYearAgoDate = () => {
    const today = new Date();
    const oneYearAgo = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
    const year = oneYearAgo.getFullYear();
    const month = String(oneYearAgo.getMonth() + 1).padStart(2, '0');
    const day = String(oneYearAgo.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  /** Parse Yardi/API date string (ISO or MM/DD/YYYY) to YYYY-MM-DD for storage */
  const parseYardiDateToISO = (dateStr: string | undefined): string => {
    if (!dateStr || !dateStr.trim()) return '';
    const d = new Date(dateStr.trim());
    if (isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  /** Format YYYY-MM-DD to MM/DD/YYYY for display */
  const formatDateMMDDYYYY = (isoDate: string | undefined): string => {
    if (!isoDate || !isoDate.trim()) return '';
    const d = new Date(isoDate.trim());
    if (isNaN(d.getTime())) return '';
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    const year = d.getUTCFullYear();
    return `${month}/${day}/${year}`;
  };

  // Helper functions for dynamic text based on animal type
  // Only show ESA/SA text if user has completed the animal type step (step 2)
  const getAnimalTypeLabel = (petIndex = currentPetIndex) => {
    if (step >= 2) {
      const pet = formData.pets?.[petIndex] || formData.pets?.[0];
      const animalType = pet?.animalType;
      if (animalType === 'esa') return 'Emotional Support Animal';
      if (animalType === 'sa') return 'Service Animal';
      return 'Pet';
    }
    return 'Animal';
  };

  const getSidebarTitle = () => {
    if (step >= 2) {
      const currentPet = formData.pets?.[currentPetIndex] || formData.pets?.[0];
      const animalType = currentPet?.animalType;
      if (animalType === 'esa') return 'Add an Emotional Support Animal';
      if (animalType === 'sa') return 'Add a Service Animal';
      return 'Add a Pet';
    }
    return 'Add an Animal';
  };

  const getStep2Label = () => {
    if (step >= 2) {
      const currentPet = formData.pets?.[currentPetIndex] || formData.pets?.[0];
      const animalType = currentPet?.animalType;
      if (animalType === 'esa') return 'Emotional Support Animal Details';
      if (animalType === 'sa') return 'Service Animal Details';
      return 'Pet Details';
    }
    return 'Animal Details';
  };

  // Get dynamic label for titles - lowercase version
  const getAnimalTypeLabelLower = (petIndex = currentPetIndex) => {
    return getAnimalTypeLabel(petIndex).toLowerCase();
  };

  useEffect(() => {
    if (isOpen) {
      if (!draftPet && !isMultiPetWorkflow) {
        resetForm();
      }
      setIsStep1Loading(true);
      Promise.all([fetchProperties(), fetchUserDetails()])
        .finally(() => setIsStep1Loading(false));
    }
  }, [isOpen]);

  // When we have Yardi move dates, keep all pets in sync (e.g. when user adds another pet)
  useEffect(() => {
    if (!gcResidentInfo?.move_in || !formData.pets?.length) return;
    const moveInISO = parseYardiDateToISO(gcResidentInfo.move_in);
    const moveOutISO = parseYardiDateToISO(gcResidentInfo.expected_move_out);
    if (!moveInISO) return;
    const needsUpdate = formData.pets.some(
      p => !p.move_in_date || p.move_in_date !== moveInISO
    );
    if (!needsUpdate) return;
    setFormData(prev => ({
      ...prev,
      pets: prev.pets!.map(p => ({
        ...p,
        move_in_date: moveInISO || p.move_in_date,
        move_out_date: moveOutISO || p.move_out_date
      }))
    }));
  }, [gcResidentInfo?.move_in, gcResidentInfo?.expected_move_out, formData.pets?.length]);

  useEffect(() => {
    if (step <= 2 || !formData.pets || formData.pets.length === 0) return;

    let currentStepCount = 2;

    for (let petIndex = 0; petIndex < formData.pets.length; petIndex++) {
      if (step === currentStepCount + 1) return;
      if (step === currentStepCount + 2) return;
      if (step === currentStepCount + 3) return;
      if (step === currentStepCount + 4) return;

      currentStepCount += 4;
    }
  }, [step, formData.pets.length]);

  const fetchProperties = async () => {
    try {
      const data = await petService.getPropertiesList();
      setProperties(data);
    } catch (error) {
      console.error('Error fetching properties:', error);
      setProperties([
        { id: '1', name: 'Sunset Ridge Apartments', address: '123 Sunset Blvd, Los Angeles, CA 90028' },
        { id: '2', name: 'Park View Towers', address: '456 Park Ave, Los Angeles, CA 90067' },
        { id: '3', name: 'Green Valley Townhomes', address: '789 Valley Road, Pasadena, CA 91101' }
      ]);
    } finally {
      setLoadingProperties(false);
    }
  };

  const getApiBase = () => {
    return (typeof window !== 'undefined' && window.location.hostname === 'localhost')
      ? 'http://localhost:8000/api/'
      : (import.meta.env.VITE_API_URL || 'https://pawtected-client-dev-b941ac1d80e9.herokuapp.com/api/');
  };

  const fetchUserDetails = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        console.warn('⚠️ No auth token found, cannot fetch user details');
        return;
      }

      // Step 1: Fetch user profile to get email
      let userEmail = '';
      try {
        const response = await fetch(`${getApiBase()}users/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const userData = await response.json();
          userEmail = userData.email || '';
          console.log('📋 User profile fetched:', { email: userEmail });

          // Set email + unit immediately (unit comes from users.unit, which
          // is the merged GC/resident-entered value — see
          // _update_user_property_info on the backend).
          //
          // PAW-143: never overwrite the unit of a resumed draft. A draft's
          // unit is what the resident typed, and this effect runs on every
          // open (the guard at the top of this component only skips
          // resetForm(), not this fetch). Letting the live lease value land
          // here would silently discard an in-progress edit before it is
          // saved back — and the backend deliberately returns the draft's own
          // stored unit for exactly this reason.
          setFormData(prev => ({
            ...prev,
            contactEmail: userEmail || prev.contactEmail,
            unit: draftPet ? prev.unit : (userData.unit || prev.unit || '')
          }));
          setPrefilledLeaseValues(prev => ({
            unit: draftPet ? prev.unit : (userData.unit || prev.unit),
            phone: userData.contact_phone || prev.phone,
          }));

          // Also populate phone if available from profile
          let phoneNumber = userData.phone || userData.phoneNumber || '';
          if (phoneNumber) {
            let countryCode = '+1';
            let numberOnly = phoneNumber;

            if (phoneNumber.startsWith('+')) {
              const matchingConfig = countryPhoneConfigs.find(config =>
                phoneNumber.startsWith(config.code)
              );
              if (matchingConfig) {
                countryCode = matchingConfig.code;
                numberOnly = phoneNumber.substring(matchingConfig.code.length);
                numberOnly = numberOnly.replace(/\D/g, '');
              }
            } else {
              numberOnly = phoneNumber.replace(/\D/g, '');
            }

            setFormData(prev => ({
              ...prev,
              phoneCountryCode: countryCode,
              phoneNumber: numberOnly,
              contactPhone: countryCode + numberOnly
            }));
            setSelectedCountryCode(countryCode);
          }
        } else {
          console.warn('⚠️ Could not fetch user profile:', response.status);
        }
      } catch (profileErr) {
        console.error('Error fetching user profile:', profileErr);
      }

      // Step 2: Try to fetch resident info from Get Covered (source of truth)
      if (userEmail) {
        setIsLoadingGCData(true);
        setGCDataError(null);

        try {
          console.log('🔍 Attempting to fetch resident info from Get Covered...');
          const gcInfo = await residentService.getResidentInfoByEmail(userEmail);

          if (gcInfo) {
            console.log('✅ Resident data loaded from Get Covered:', gcInfo);
            setGCResidentInfo(gcInfo);

            const gcUnit = gcInfo.unit?.trim();
            const gcPhone = gcInfo.resident_contact?.trim();
            const phoneParsed = gcPhone ? parsePhoneNumber(gcPhone) : null;

            // Parse move-in and expected move-out dates to YYYY-MM-DD for date inputs
            const moveInDate = parseDateToYMD(gcInfo.move_in);
            const moveOutDate = parseDateToYMD(gcInfo.expected_move_out);
            console.log('📅 Parsed dates - move_in:', gcInfo.move_in, '->', moveInDate, '| expected_move_out:', gcInfo.expected_move_out, '->', moveOutDate);

            // Auto-populate from GC, but only override unit/phone when GC
            // actually has a value — otherwise keep what step 1 already
            // prefilled from users.{unit, contact_phone} (the merged
            // GC-or-resident-entered value).
            //
            // PAW-143: same draft guard as the profile prefill above. For a
            // resumed draft the unit on screen is the resident's own input.
            setFormData(prev => ({
              ...prev,
              property: gcInfo.community || prev.property || '',
              unit: draftPet ? prev.unit : (gcUnit || prev.unit || ''),
              contactEmail: gcInfo.contact_email || gcInfo.resident_email || userEmail || prev.contactEmail || '',
              phoneCountryCode: phoneParsed ? phoneParsed.countryCode : prev.phoneCountryCode,
              phoneNumber: phoneParsed ? phoneParsed.number : prev.phoneNumber,
              contactPhone: gcPhone || prev.contactPhone || '',
              pets: prev.pets.map(pet => ({
                ...pet,
                move_in_date: moveInDate || pet.move_in_date,
                move_out_date: moveOutDate || pet.move_out_date
              }))
            }));

            if (phoneParsed) {
              setSelectedCountryCode(phoneParsed.countryCode);
            }
            setPrefilledLeaseValues(prev => ({
              unit: gcUnit || prev.unit,
              phone: gcPhone || prev.phone,
            }));
          } else {
            console.log('ℹ️ No resident found in Get Covered system');
          }
        } catch (gcError) {
          console.warn('⚠️ Could not load from Get Covered:', gcError);
          setGCDataError('Get Covered data unavailable');
        } finally {
          setIsLoadingGCData(false);
        }
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  // Fetch questionnaire settings when property is selected
  useEffect(() => {
    const fetchSettings = async () => {
      if (!formData.property) {
        setQuestionnaireSettings(null);
        return;
      }

      // Find the property object to get its ID
      const selectedProperty = properties.find(p => p.name === formData.property);
      const propertyLookup = selectedProperty?.id || formData.property;

      setLoadingSettings(true);
      try {
        const settings = await settingsService.getPropertySettings(propertyLookup);
        console.log('✅ Questionnaire settings loaded for property:', formData.property, settings);
        setQuestionnaireSettings(settings);
      } catch (error) {
        console.warn('⚠️ Could not fetch questionnaire settings, using defaults:', error);
        setQuestionnaireSettings(null);
      } finally {
        setLoadingSettings(false);
      }
    };

    fetchSettings();
  }, [formData.property, properties]);

  if (!isOpen) return null;

  const calculateTotalSteps = () => {
    if (!formData.pets || formData.pets.length === 0) {
      return 2;
    }
    
    const basePetSteps = 4;
    const attestationStep = 1;
    let totalSteps = 1;
    const petsToCount = isMultiPetWorkflow ? 1 : formData.pets.length;
    
    for (let i = 0; i < petsToCount; i++) {
      totalSteps += basePetSteps;
    }
    
    return totalSteps + attestationStep; // Step 6: Review & Attestation
  };

  const totalSteps = calculateTotalSteps();

  // Helper functions to check field visibility and requirements based on PMC's extra_data settings
  // shouldShowQuestion is the single visibility helper for a question object — admin's `show: false`
  // wins outright. Every render and validation site routes through here (or through isFieldVisible,
  // which delegates to it).
  const shouldShowQuestion = (q: { show?: boolean } | undefined) => {
    if (!q) return false;
    if (q.show === false) return false;
    return true;
  };

  // q2 (Animal Type) and q3 (Animal Name) are always required and visible
  // regardless of property settings — they identify the animal.
  const HARDCODED_REQUIRED_VISIBLE = new Set(['q2', 'q3']);

  // Questions hidden for Service Animals regardless of admin settings.
  const HIDE_FOR_SA = new Set(['q4']);

  const isFieldVisible = (questionId) => {
    if (HARDCODED_REQUIRED_VISIBLE.has(questionId)) return true;
    const animalType = formData.pets[currentPetIndex]?.animalType || formData.pets[0]?.animalType;
    if (HIDE_FOR_SA.has(questionId) && animalType === 'sa') return false;
    if (!questionnaireSettings?.questionnaire) return true;
    const question = questionnaireSettings.questionnaire.find(q => q.id === questionId);
    if (!question) return false;
    return shouldShowQuestion(question);
  };

  const isFieldRequired = (questionId) => {
    if (HARDCODED_REQUIRED_VISIBLE.has(questionId)) return true;
    if (!questionnaireSettings?.questionnaire) return true; // Default: all fields required if no settings
    const question = questionnaireSettings.questionnaire.find(q => q.id === questionId);
    return question ? question.required : true;
  };

  const getVisibleQuestionsByGroup = (groupKey) => {
    if (!questionnaireSettings?.questionnaire) return [];
    return questionnaireSettings.questionnaire
      .filter(q => q.show === true && q.group === groupKey)
      .sort((a, b) => a.order - b.order);
  };

  // Known question IDs that are hardcoded in the form
  const KNOWN_QUESTION_IDS = new Set([
    'q1','q2','q3','q4','q5','q6','q7','q8','q11','q12','q13','q14','q15','q16','q17','q18','q19'
  ]);

  // Get custom (admin-added) questions for a specific group
  const getCustomQuestions = (group: string) => {
    if (!questionnaireSettings?.questionnaire) return [];
    return questionnaireSettings.questionnaire
      .filter(q => !KNOWN_QUESTION_IDS.has(q.id) && q.group === group && shouldShowQuestion(q))
      .sort((a, b) => a.order - b.order);
  };

  // Update a custom field value on the current pet
  const handleCustomFieldChange = (questionId: string, value: string) => {
    setFormData(prev => {
      const newPets = prev.pets.map((pet, index) => {
        if (index === currentPetIndex) {
          return { ...pet, customFields: { ...pet.customFields, [questionId]: value } };
        }
        return pet;
      });
      return { ...prev, pets: newPets };
    });
    // Clear error for this field
    if (customFieldErrors[questionId]) {
      setCustomFieldErrors(prev => ({ ...prev, [questionId]: '' }));
    }
  };

  // Render custom questions for a group — reuses existing field styles. File uploads
  // belong to DocumentUploadView, so we skip them here.
  const renderCustomFields = (group: string) => {
    const questions = getCustomQuestions(group).filter(q => q.type !== 'file');
    if (questions.length === 0) return null;

    const currentPet = formData.pets[currentPetIndex] || formData.pets[0];

    return questions.map(q => {
      const value = currentPet.customFields?.[q.id] || '';
      const error = customFieldErrors[q.id];

      return (
        <div key={q.id} style={{ order: q.order + 100 }}>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            {q.question} {q.required && <span className="text-red-500">*</span>}
          </label>

          {q.type === 'select' && q.options?.length > 0 ? (
            <select
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                error ? 'border-red-500' : 'border-gray-300'
              }`}
              value={value}
              onChange={(e) => handleCustomFieldChange(q.id, e.target.value)}
              required={q.required}
            >
              <option value="">Select</option>
              {q.options.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          ) : q.type === 'date' ? (
            <input
              type="date"
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors [&::-webkit-calendar-picker-indicator]:cursor-pointer ${
                error ? 'border-red-500' : 'border-gray-300'
              }`}
              value={value}
              onChange={(e) => handleCustomFieldChange(q.id, e.target.value)}
              required={q.required}
            />
          ) : q.type === 'number' ? (
            <input
              type="number"
              placeholder={`Enter ${q.question.toLowerCase()}`}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                error ? 'border-red-500' : 'border-gray-300'
              }`}
              value={value}
              onChange={(e) => handleCustomFieldChange(q.id, e.target.value)}
              required={q.required}
            />
          ) : q.type === 'tel' ? (
            <input
              type="tel"
              placeholder={`Enter ${q.question.toLowerCase()}`}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                error ? 'border-red-500' : 'border-gray-300'
              }`}
              value={value}
              onChange={(e) => handleCustomFieldChange(q.id, e.target.value)}
              required={q.required}
            />
          ) : (
            <input
              type="text"
              placeholder={`Enter ${q.question.toLowerCase()}`}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                error ? 'border-red-500' : 'border-gray-300'
              }`}
              value={value}
              onChange={(e) => handleCustomFieldChange(q.id, e.target.value)}
              required={q.required}
            />
          )}

          {error && (
            <p className="text-xs text-red-600 mt-1 animate-pulse">{error}</p>
          )}
        </div>
      );
    });
  };

  // Helper functions for general settings enforcement
  const isPetTypeAllowed = (petType) => {
    const restrictions = questionnaireSettings?.general?.petTypeRestrictions || [];
    if (restrictions.length === 0) return true; // No restrictions
    // petType is 'dog', 'cat', or 'other'
    // restrictions array contains capitalized names like 'Dog', 'Cat'
    const normalizedRestrictions = restrictions.map(r => r.toLowerCase());
    return !normalizedRestrictions.includes(petType.toLowerCase());
  };

  const isBreedAllowed = (breed) => {
    // Check breed restrictions (for dog/cat breeds)
    const breedRestrictions = questionnaireSettings?.general?.breedRestrictions || [];
    if (breedRestrictions.includes(breed)) return false;

    // Also check pet type restrictions (for "other" pet types like Guinea Pig, Bird, etc.)
    const petTypeRestrictions = questionnaireSettings?.general?.petTypeRestrictions || [];
    const normalizedRestrictions = petTypeRestrictions.map(r => r.toLowerCase());
    if (normalizedRestrictions.includes(breed.toLowerCase())) return false;

    return true;
  };

  const getWeightLimit = () => {
    return questionnaireSettings?.general?.weightLimit || 0; // 0 means no limit
  };

  // Get the display order for a question (used for CSS flexbox ordering)
  const getQuestionOrder = (questionId: string): number => {
    if (!questionnaireSettings?.questionnaire) return 99; // Default high number if no settings
    const question = questionnaireSettings.questionnaire.find(q => q.id === questionId);
    return question?.order ?? 99;
  };

  // Get the label for a question from settings, falling back to the provided default
  const getFieldLabel = (questionId: string, defaultLabel: string): string => {
    if (!questionnaireSettings?.questionnaire) return defaultLabel;
    const question = questionnaireSettings.questionnaire.find(q => q.id === questionId);
    return question?.question || defaultLabel;
  };

  const isWeightAllowed = (weight) => {
    const limit = getWeightLimit();
    if (!limit || limit === 0) return true; // No limit
    return parseFloat(weight) <= limit;
  };

  const handleInputChange = (field, value) => {
    console.log('🔍 Input change:', field, value, 'currentPetIndex:', currentPetIndex);
    if (field.startsWith('pet.')) {
      const petField = field.replace('pet.', '');
      setFormData(prev => {
        console.log('📊 Previous pets:', prev.pets);
        const newPets = prev.pets.map((pet, index) => {
          if (index === currentPetIndex) {
            const updatedPet = { ...pet, [petField]: value };
            if (petField === 'petType') {
              updatedPet.breed = '';
              updatedPet.customBreed = '';
              if (value !== 'other') {
                updatedPet.customPetType = '';
              }
            }
            if (petField === 'breed' && value !== 'Other') {
              updatedPet.customBreed = '';
            }
            if (petField === 'color' && value !== 'Other') {
              updatedPet.customColor = '';
            }
            if (petField === 'markings' && value !== 'Other') {
              updatedPet.customMarkings = '';
            }
            return updatedPet;
          }
          return pet;
        });
        console.log('📊 New pets:', newPets);
        return {
          ...prev,
          pets: newPets
        };
      });

      if (petField === 'animalType') {
        const freshPet = initialPet();
        setFormData(prev => ({
          ...prev,
          pets: prev.pets.map((pet, i) =>
            i === currentPetIndex
              ? {
                  ...freshPet,
                  animalType: value,
                  isESA: value === 'esa',
                  move_in_date: pet.move_in_date,
                  move_out_date: pet.move_out_date,
                }
              : pet
          ),
        }));
        setPetNameValidationError('');
        setVetPhoneValidationError('');
        setValidationErrors({...INITIAL_VALIDATION_ERRORS});
        setCustomFieldErrors({});
        setUploadStates(initialUploadStates);
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
        setPetTypeSuggestions([]);
        setShowPetTypeSuggestions(false);
        setShowPhotoPreview(false);
        setAttestationAcknowledged(false);
        return;
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }

    const fieldKey = field.replace('pet.', '');
    if (validationErrors[fieldKey]) {
      setValidationErrors(prev => ({ ...prev, [fieldKey]: '' }));
    }
  };

  const handleFileUpload = async (type, file) => {
    setUploadStates(prev => ({
      ...prev,
      [type]: { status: 'uploading', file: null }
    }));
    
    setTimeout(() => {
      setUploadStates(prev => ({
        ...prev,
        [type]: { status: 'success', file: file }
      }));
    }, 2000);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateRequired = (value, fieldName) => {
    const trimmedValue = typeof value === 'string' ? value.trim() : value;
    if (!trimmedValue) {
      return `${fieldName} is required`;
    }
    return '';
  };

  const validateNumber = (value, fieldName, min = 0, max = null) => {
    if (!value) {
      return `${fieldName} is required`;
    }
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return `${fieldName} must be a valid number`;
    }
    
    if (numValue < min) {
      return `${fieldName} must be at least ${min}`;
    }
    
    if (max !== null && numValue > max) {
      return `${fieldName} cannot exceed ${max}`;
    }
    
    return '';
  };

  const validateStep1 = () => {
    const errors = {};

    errors.property = validateRequired(formData.property, 'Property');
    // Unit + phone are always optional at step 1 with no validation —
    // empty or partial values are accepted and don't block the step.

    if (!formData.contactEmail.trim()) {
      errors.contactEmail = 'Email is required';
    } else if (!validateEmail(formData.contactEmail)) {
      errors.contactEmail = 'Please enter a valid email address';
    }

    setValidationErrors(prev => ({ ...prev, ...errors }));

    const hasErrors = Object.values(errors).some(error => error !== '');
    return !hasErrors;
  };

  const validateCurrentPetStep2 = () => {
    const currentPet = formData.pets[currentPetIndex] || formData.pets[0];
    const errors = {};

    // Only validate pet name if q3 is required
    const isPetNameValid = (isFieldVisible('q3') && isFieldRequired('q3')) ? validateCurrentPetName() : true;

    // q1: Pet Photo — admin's required flag wins for all animal types
    if (isFieldVisible('q1') && isFieldRequired('q1')) {
      const photo = currentPet.photos?.[0];
      const hasPhoto = photo?.file || hasPersistedPetPhotoPreview(currentPet);
      if (!hasPhoto) {
        errors.photos = 'Pet photo is required';
      } else {
        errors.photos = '';
      }
    } else {
      errors.photos = '';
    }

    // q2: Pet Type
    if (isFieldVisible('q2') && !currentPet.petType) {
      errors.petType = 'Please select a pet type';
    } else {
      errors.petType = '';
    }

    // Validate customPetType when petType is 'other'
    if (currentPet.petType === 'other' && !currentPet.customPetType?.trim()) {
      errors.customPetType = 'Please specify the pet type';
    } else {
      errors.customPetType = '';
    }

    // q4: Breed - only validate if required
    if (isFieldVisible('q4') && isFieldRequired('q4')) {
      if (currentPet.petType === 'other') {
        errors.breed = currentPet.breed?.trim() ? '' : 'Breed is required';
      } else {
        errors.breed = validateRequired(currentPet.breed, 'Breed');
        if (!errors.breed && currentPet.breed === 'Other' && !currentPet.customBreed?.trim()) {
          errors.breed = 'Please specify the breed';
        }
      }
    } else {
      errors.breed = '';
    }

    // q6: Age - only validate if required
    if (isFieldVisible('q6') && isFieldRequired('q6')) {
      if (currentPet.age === null || currentPet.age === undefined || currentPet.age === '') {
        errors.age = 'Age is required';
      } else {
        errors.age = '';
      }
    } else {
      errors.age = '';
    }

    // q5: Weight - only validate if required (property weight limit is a warning, not a blocker)
    const weightValue = parseFloat(currentPet.weight?.value || 0);
    if (isFieldVisible('q5') && isFieldRequired('q5')) {
      if (!currentPet.weight?.value || weightValue <= 0) {
        errors.weight = 'Weight is required';
      } else if (isNaN(weightValue)) {
        errors.weight = 'Weight must be a valid number';
      } else if (weightValue < 0.1) {
        errors.weight = 'Weight must be at least 0.1';
      } else {
        errors.weight = '';
      }
    } else {
      // Still validate format if value is provided, but don't require it
      if (currentPet.weight?.value && (isNaN(weightValue) || weightValue < 0)) {
        errors.weight = 'Weight must be a valid positive number';
      } else {
        errors.weight = '';
      }
    }

    // q7: Gender - only validate if required
    if (isFieldVisible('q7') && isFieldRequired('q7')) {
      if (!currentPet.gender) {
        errors.gender = 'Gender is required';
      } else {
        errors.gender = '';
      }
    } else {
      errors.gender = '';
    }

    // q8: Spayed/Neutered - only validate if required
    if (isFieldVisible('q8') && isFieldRequired('q8')) {
      if (currentPet.spayed_neutered === null) {
        errors.spayed_neutered = 'Please select spayed/neutered status';
      } else {
        errors.spayed_neutered = '';
      }
    } else {
      errors.spayed_neutered = '';
    }

    // q11: Color - only validate if required
    if (isFieldVisible('q11') && isFieldRequired('q11')) {
      if (!currentPet.color || currentPet.color.trim() === '') {
        errors.color = 'Primary color is required';
      } else if (currentPet.color === 'Other' && !currentPet.customColor?.trim()) {
        errors.color = 'Please specify the color';
      } else {
        errors.color = '';
      }
    } else {
      errors.color = '';
    }

    // q12: Markings/Patterns - only validate if required
    if (isFieldVisible('q12') && isFieldRequired('q12')) {
      if (!currentPet.markings || currentPet.markings.trim() === '') {
        errors.markings = 'Markings/Patterns is required';
      } else if (currentPet.markings === 'Other' && !currentPet.customMarkings?.trim()) {
        errors.markings = 'Please specify the markings/pattern';
      } else {
        errors.markings = '';
      }
    } else {
      errors.markings = '';
    }

    // q19: Previous Pet Ownership
    if (isFieldVisible('q19') && isFieldRequired('q19')) {
      if (!currentPet.previousPetOwnership) {
        errors.previousPetOwnership = 'Please select your previous pet ownership history';
      } else {
        errors.previousPetOwnership = '';
      }
    } else {
      errors.previousPetOwnership = '';
    }

    setValidationErrors(prev => ({ ...prev, ...errors }));

    // Validate custom fields for animal_info group
    const customErrors: Record<string, string> = {};
    getCustomQuestions('animal_info').forEach(q => {
      if (q.show !== false && q.required && !currentPet.customFields?.[q.id]?.trim()) {
        customErrors[q.id] = `${q.question} is required`;
      }
    });
    setCustomFieldErrors(prev => ({ ...prev, ...customErrors }));

    const hasCustomErrors = Object.values(customErrors).some(e => e !== '');
    const hasErrors = Object.values(errors).some(error => error !== '') || !isPetNameValid || hasCustomErrors;
    return !hasErrors;
  };

  const validateCurrentPetStep4 = () => {
    const currentPet = formData.pets[currentPetIndex] || formData.pets[0];

    // Vet info is optional for Service Animals
    if (currentPet.animalType === 'sa') return true;

    const errors = {};

    // q13: Vet Clinic Name - only validate if required
    if (isFieldVisible('q13') && isFieldRequired('q13')) {
      errors.vetClinic = validateRequired(currentPet.vetClinic, 'Veterinary clinic name');
    } else {
      errors.vetClinic = '';
    }

    // q14: Vet Address - only validate if required
    if (isFieldVisible('q14') && isFieldRequired('q14')) {
      errors.vetAddress = validateRequired(currentPet.vetAddress, 'Veterinary clinic address');
    } else {
      errors.vetAddress = '';
    }

    // q15: Vet Phone - only validate if required
    const isVetPhoneValid = (isFieldVisible('q15') && isFieldRequired('q15')) ? validateCurrentVetPhoneNumber() : true;

    // q16: Last Visit Date - only validate if required
    if (isFieldVisible('q16') && isFieldRequired('q16')) {
      if (!currentPet.lastVisit) {
        errors.lastVisit = 'Last visit date is required';
      } else {
        const todayStr = getTodayDate();
        const oneYearAgoStr = getOneYearAgoDate();

        if (currentPet.lastVisit > todayStr) {
          errors.lastVisit = 'Last visit date cannot be in the future';
        } else if (currentPet.lastVisit < oneYearAgoStr) {
          errors.lastVisit = 'Last visit date must be within the past year';
        } else {
          errors.lastVisit = '';
        }
      }
    } else {
      // Still validate format if value is provided
      if (currentPet.lastVisit) {
        const todayStr = getTodayDate();
        const oneYearAgoStr = getOneYearAgoDate();
        if (currentPet.lastVisit > todayStr) {
          errors.lastVisit = 'Last visit date cannot be in the future';
        } else if (currentPet.lastVisit < oneYearAgoStr) {
          errors.lastVisit = 'Last visit date must be within the past year';
        } else {
          errors.lastVisit = '';
        }
      } else {
        errors.lastVisit = '';
      }
    }

    setValidationErrors(prev => ({ ...prev, ...errors }));

    // Validate custom fields for vet_info group
    const customErrors: Record<string, string> = {};
    getCustomQuestions('vet_info').forEach(q => {
      if (q.show !== false && q.required && !currentPet.customFields?.[q.id]?.trim()) {
        customErrors[q.id] = `${q.question} is required`;
      }
    });
    setCustomFieldErrors(prev => ({ ...prev, ...customErrors }));

    const hasCustomErrors = Object.values(customErrors).some(e => e !== '');
    const hasErrors = Object.values(errors).some(error => error !== '') || !isVetPhoneValid || hasCustomErrors;
    return !hasErrors;
  };

  const validateCurrentPetESA = () => {
    const currentPet = formData.pets[currentPetIndex] || formData.pets[0];
    if (currentPet.animalType !== 'esa') return true;
    
    const errors = {};
    
    errors.esaProfessional = validateRequired(currentPet.esaProfessional, 'Licensed professional name');
    errors.esaLetterDate = validateRequired(currentPet.esaLetterDate, 'Letter issue date');
    
    if (currentPet.esaLetterDate) {
      const letterDate = new Date(currentPet.esaLetterDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (letterDate > today) {
        errors.esaLetterDate = 'Letter date cannot be in the future';
      }
      
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      
      if (letterDate < oneYearAgo) {
        errors.esaLetterDate = 'Emotional Support Animal letter should be dated within the last year';
      }
    }
    
    setValidationErrors(prev => ({ ...prev, ...errors }));
    
    const hasErrors = Object.values(errors).some(error => error !== '');
    return !hasErrors;
  };

  const handleCountryCodeChange = (code) => {
    setSelectedCountryCode(code);
    handleInputChange('phoneCountryCode', code);
    handleInputChange('contactPhone', code + formData.phoneNumber);
  };

  const handlePhoneNumberChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '');
    const currentConfig = countryPhoneConfigs.find(c => c.code === selectedCountryCode) || countryPhoneConfigs[0];
    const truncatedNumber = digitsOnly.substring(0, currentConfig.maxDigits);
    
    handleInputChange('phoneNumber', truncatedNumber);
    handleInputChange('contactPhone', selectedCountryCode + truncatedNumber);
  };

  const handleCurrentVetCountryCodeChange = (code) => {
    setSelectedVetCountryCode(code);
    handleInputChange('pet.vetPhoneCountryCode', code);
    setVetPhoneValidationError('');
    const currentPet = formData.pets[currentPetIndex] || formData.pets[0];
    handleInputChange('pet.vetPhone', code + currentPet.vetPhoneNumber);
    
    setTimeout(() => {
      if (currentPet.vetPhoneNumber && currentPet.vetPhoneNumber.length >= 7) {
        validateCurrentVetPhoneNumber();
      }
    }, 100);
  };

  const handleCurrentVetPhoneNumberChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '');
    const currentConfig = countryPhoneConfigs.find(c => c.code === selectedVetCountryCode) || countryPhoneConfigs[0];
    const truncatedNumber = digitsOnly.substring(0, currentConfig.maxDigits);
    
    handleInputChange('pet.vetPhoneNumber', truncatedNumber);
    handleInputChange('pet.vetPhone', selectedVetCountryCode + truncatedNumber);
    
    if (vetPhoneValidationError) {
      setVetPhoneValidationError('');
    }
  };

  const validateCurrentVetPhoneNumber = () => {
    const currentPet = formData.pets[currentPetIndex] || formData.pets[0];
    
    // Vet phone is optional for Service Animals
    if (currentPet.animalType === 'sa') {
      setVetPhoneValidationError('');
      return true;
    }
    
    const currentConfig = countryPhoneConfigs.find(c => c.code === selectedVetCountryCode) || countryPhoneConfigs[0];
    
    if (!currentPet.vetPhoneNumber || currentPet.vetPhoneNumber.trim() === '') {
      setVetPhoneValidationError('Vet phone number is required');
      return false;
    }
    
    if (currentPet.vetPhoneNumber.length < 7) {
      setVetPhoneValidationError('Phone number is too short');
      return false;
    }
    
    if (currentPet.vetPhoneNumber.length < currentConfig.maxDigits) {
      setVetPhoneValidationError(`Phone number should be ${currentConfig.maxDigits} digits for ${currentConfig.country}`);
      return false;
    }
    
    if (formData.phoneNumber && currentPet.vetPhoneNumber && 
        formData.phoneNumber.length >= 7 && currentPet.vetPhoneNumber.length >= 7) {
      const userFullPhone = formData.phoneCountryCode + formData.phoneNumber;
      const vetFullPhone = selectedVetCountryCode + currentPet.vetPhoneNumber;
      
      if (userFullPhone === vetFullPhone) {
        setVetPhoneValidationError('Veterinarian phone number cannot be the same as your contact number');
        return false;
      }
    }
    
    setVetPhoneValidationError('');
    return true;
  };

  const handleCurrentPetNameChange = (value) => {
    console.log('🐾 Pet name change:', value, 'currentPetIndex:', currentPetIndex);
    const validCharsPattern = /^[a-zA-Z\s\-']*$/;
    
    if (validCharsPattern.test(value)) {
      handleInputChange('pet.petName', value);
      setPetNameValidationError('');
    } else {
      setPetNameValidationError('Only letters, spaces, hyphens, and apostrophes are allowed');
    }
  };

  const validateCurrentPetName = () => {
    const currentPet = formData.pets[currentPetIndex] || formData.pets[0];
    const name = currentPet.petName.trim();
    
    if (!name) {
      setPetNameValidationError('Pet name is required');
      return false;
    }
    
    if (name.length < 2) {
      setPetNameValidationError('Pet name must be at least 2 characters');
      return false;
    }
    
    if (/^\d+$/.test(name)) {
      setPetNameValidationError('Pet name cannot be only numbers');
      return false;
    }
    
    const validPattern = /^[a-zA-Z\s\-']+$/;
    if (!validPattern.test(name)) {
      setPetNameValidationError('Only letters, spaces, hyphens, and apostrophes are allowed');
      return false;
    }
    
    if (!/[a-zA-Z]/.test(name)) {
      setPetNameValidationError('Pet name must contain at least one letter');
      return false;
    }
    
    if (/[\-']{3,}/.test(name)) {
      setPetNameValidationError('Too many consecutive special characters');
      return false;
    }
    
    if (name.length > 20) {
      setPetNameValidationError('Pet name is too long (maximum 20 characters)');
      return false;
    }
    
    setPetNameValidationError('');
    return true;
  };

  const getCurrentStepInfo = () => {
    if (step === 1) return { type: 'property', title: 'Resident Information' };
    
    const petIndex = isMultiPetWorkflow ? currentPetIndex : 0;
    const currentStepCount = 1;
    const animalLabel = getAnimalTypeLabel(petIndex);
    
    if (step === currentStepCount + 1) {
      return { type: 'esaInfo', title: 'Animal Type', petIndex };
    }
    if (step === currentStepCount + 2) {
      return { type: 'petDetails', title: `${animalLabel} Details`, petIndex };
    }
    if (step === currentStepCount + 3) {
      return { type: 'vetInfo', title: 'Veterinary Information', petIndex };
    }
    if (step === currentStepCount + 4) {
      return { type: 'documents', title: 'Document Upload', petIndex };
    }
    if (step === totalSteps) {
      return { type: 'attestation', title: 'Review & Attestation', petIndex };
    }
    
    return { type: 'unknown', title: 'Unknown Step' };
  };

  // True when the resident has typed a value into a field that was empty
  // on the lease at modal-open time. Drives the "Update & Continue" button
  // label and triggers a write-back to users.{contact_phone, unit} before
  // advancing past step 1. Compares the live form value against the
  // snapshot — re-clicking Next with unchanged values is a no-op (no PUT).
  const userFilledMissingGCFields = (): boolean => {
    if (!gcResidentInfo) return false;
    const prefilledMissingPhone = !prefilledLeaseValues.phone.trim();
    const prefilledMissingUnit = !prefilledLeaseValues.unit.trim();
    const userHasPhone = !!formData.contactPhone?.trim();
    const userHasUnit = !!formData.unit?.trim();
    return (prefilledMissingPhone && userHasPhone) || (prefilledMissingUnit && userHasUnit);
  };

  // Helper function to check if current step is valid for Next button
  const isCurrentStepValid = () => {
    const stepInfo = getCurrentStepInfo();
    const currentPet = formData.pets[currentPetIndex] || formData.pets[0];
    
    switch (stepInfo.type) {
      case 'property':
        if (!formData.property || !formData.contactEmail) return false;
        break;
        
      case 'petDetails': {
        const moveInFromYardi = !!gcResidentInfo?.move_in;
        const moveOutFromYardi = !!gcResidentInfo?.expected_move_out;

        // q2: Pet Type (always required when visible — core identifier)
        if (isFieldVisible('q2') && !currentPet.petType) return false;
        if (currentPet.petType === 'other' && !currentPet.customPetType?.trim()) return false;

        // q3: Pet Name
        if (isFieldVisible('q3') && isFieldRequired('q3') && !currentPet.petName) return false;

        // q4: Breed
        if (isFieldVisible('q4') && isFieldRequired('q4')) {
          if (!currentPet.breed?.trim()) return false;
          if (currentPet.petType !== 'other' && currentPet.breed === 'Other' && !currentPet.customBreed?.trim()) return false;
        }

        // q6: Age
        if (isFieldVisible('q6') && isFieldRequired('q6') &&
            (currentPet.age === null || currentPet.age === undefined || currentPet.age === '')) return false;

        // q5: Weight
        if (isFieldVisible('q5') && isFieldRequired('q5') && !currentPet.weight?.value) return false;

        // q7: Gender
        if (isFieldVisible('q7') && isFieldRequired('q7') && !currentPet.gender) return false;

        // q8: Spayed/Neutered
        if (isFieldVisible('q8') && isFieldRequired('q8') && currentPet.spayed_neutered === null) return false;

        // q11: Color
        if (isFieldVisible('q11') && isFieldRequired('q11')) {
          if (!currentPet.color) return false;
          if (currentPet.color === 'Other' && !currentPet.customColor?.trim()) return false;
        }

        // q12: Markings
        if (isFieldVisible('q12') && isFieldRequired('q12')) {
          if (!currentPet.markings || currentPet.markings.trim() === '') return false;
          if (currentPet.markings === 'Other' && !currentPet.customMarkings?.trim()) return false;
        }

        // q19: Previous Pet Ownership
        if (isFieldVisible('q19') && isFieldRequired('q19') && !currentPet.previousPetOwnership) return false;

        // q1: Pet Photo — admin's required flag wins for all animal types
        if (isFieldVisible('q1') && isFieldRequired('q1')) {
          const photo = currentPet.photos?.[0];
          const hasPhoto = photo?.file || hasPersistedPetPhotoPreview(currentPet);
          if (!hasPhoto) return false;
        }

        // Admin custom questions for animal_info group
        for (const q of getCustomQuestions('animal_info')) {
          if (q.required && !currentPet.customFields?.[q.id]?.trim()) return false;
        }

        if (petNameValidationError) return false;
        break;
      }
        
      case 'esaInfo':
        if (currentPet.animalType === 'esa') {
          if (!currentPet.esaProfessional || !currentPet.esaLetterDate) {
            return false;
          }
          // Check ESA date validation
          if (currentPet.esaLetterDate) {
            const letterDate = new Date(currentPet.esaLetterDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const oneYearAgo = new Date();
            oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

            if (letterDate > today || letterDate < oneYearAgo) {
              return false;
            }
          }
        }
        break;
        
      case 'vetInfo':
        if (currentPet.animalType === 'sa') {
          return true;
        }

        // q13: Vet Clinic Name
        if (isFieldVisible('q13') && isFieldRequired('q13') && !currentPet.vetClinic) return false;

        // q14: Vet Address
        if (isFieldVisible('q14') && isFieldRequired('q14') && !currentPet.vetAddress) return false;

        // q15: Vet Phone
        if (isFieldVisible('q15') && isFieldRequired('q15') && !currentPet.vetPhoneNumber) return false;

        // q16: Last Visit Date
        if (isFieldVisible('q16') && isFieldRequired('q16') && !currentPet.lastVisit) return false;

        // Admin custom questions for vet_info group
        for (const q of getCustomQuestions('vet_info')) {
          if (q.required && !currentPet.customFields?.[q.id]?.trim()) return false;
        }

        if (vetPhoneValidationError) return false;
        if (currentPet.lastVisit) {
          const todayStr = getTodayDate();
          const oneYearAgoStr = getOneYearAgoDate();
          if (currentPet.lastVisit > todayStr || currentPet.lastVisit < oneYearAgoStr) {
            return false;
          }
        }
        break;

      case 'documents': {
        // Service Animals skip docs entirely.
        if (currentPet.animalType === 'sa') return true;

        const docs = petDocuments[`pet_${currentPetIndex}`] || [];
        const uploadedTypes = new Set(docs.map(d => d.docType));

        // ESA Letter is required for ESA pets.
        if (currentPet.animalType === 'esa' && !uploadedTypes.has('esa-letter')) return false;

        // Every required file-type question in the documents group must be uploaded.
        const requiredFileDocs = (questionnaireSettings?.questionnaire || []).filter(q =>
          q.group === 'documents' && q.type === 'file' && q.show !== false && q.required === true
        );
        for (const q of requiredFileDocs) {
          if (!uploadedTypes.has(q.id)) return false;
        }
        break;
      }

      case 'attestation':
        return attestationAcknowledged;

      default:
        break;
    }

    return true;
  };

  const handleNext = async () => {
    let isValid = false;
    const stepInfo = getCurrentStepInfo();

    // Check per-lease pet cap on property step. Prefer the live lease
    // summary from the backend (counts co-residents' pets), fall back to
    // the raw property setting if the summary isn't loaded yet.
    if (stepInfo.type === 'property') {
      const summaryMax = leasePetSummary?.max_allowed;
      const summaryRemaining = leasePetSummary?.remaining;
      const settingsMax = questionnaireSettings?.general?.maxPetsPerUnit;
      const maxPets = summaryMax ?? settingsMax;

      // Each questionnaire run registers exactly one pet. Multi-pet batch
      // size (petQuantity) must not drive this check — otherwise pet 2 of 2
      // fails after pet 1 already consumed a lease slot.
      const petsAddingNow = 1;

      if (typeof summaryRemaining === 'number' && petsAddingNow > summaryRemaining) {
        const existing = leasePetSummary?.active_pet_count ?? 0;
        alert(leaseCapBlockedMessage(existing, maxPets, summaryRemaining, dashboardDraftCount));
        return;
      }
      if (typeof summaryRemaining !== 'number' && maxPets && petsAddingNow > maxPets) {
        alert(
          `This property allows a maximum of ${maxPets} pet(s) per unit. ${leaseCapFullDraftHint(dashboardDraftCount)}`
        );
        return;
      }
    }

    switch (stepInfo.type) {
      case 'property':
        isValid = validateStep1();
        if (isValid && userFilledMissingGCFields()) {
          try {
            const updates: { contact_phone?: string; unit?: string } = {};
            if (!prefilledLeaseValues.phone.trim() && formData.contactPhone?.trim()) {
              updates.contact_phone = formData.contactPhone.trim();
            }
            if (!prefilledLeaseValues.unit.trim() && formData.unit?.trim()) {
              updates.unit = formData.unit.trim();
            }
            if (Object.keys(updates).length > 0) {
              await petService.updateUserProfile(updates);
              setPrefilledLeaseValues(prev => ({
                unit: updates.unit ?? prev.unit,
                phone: updates.contact_phone ?? prev.phone,
              }));
            }
          } catch (err) {
            console.error('❌ Failed to update profile with resident-entered phone/unit:', err);
            alert('Could not save your phone/unit to your profile. Please try again.');
            return;
          }
        }
        break;
      case 'petDetails':
        isValid = validateCurrentPetStep2();
        break;
      case 'esaInfo': {
        const currentPet = formData.pets[currentPetIndex] || formData.pets[0];
        if (currentPet.animalType === 'esa') {
          isValid = validateCurrentPetESA();
        } else {
          isValid = true;
        }
        break;
      }
      case 'vetInfo':
        isValid = validateCurrentPetStep4();
        break;
      case 'documents': {
        isValid = isCurrentStepValid();
        if (isValid) {
          const currentPet = formData.pets[currentPetIndex] || formData.pets[0];
          const docs = petDocuments[`pet_${currentPetIndex}`] || [];
          const docTypeDefs = [
            ...(currentPet.animalType === 'esa'
              ? [{ id: 'esa-letter', name: 'Emotional Support Animal Letter' }]
              : []),
            ...((questionnaireSettings?.questionnaire || [])
              .filter(q => q.group === 'documents' && q.type === 'file')
              .map(q => ({ id: q.id, name: q.question }))),
          ];
          const documentsForBackend = docs.map(doc => {
            const def = docTypeDefs.find(d => d.id === doc.docType);
            const displayName = def?.name || doc.docType;
            const ext = (doc.name || '').split('.').pop() || '';
            return {
              tempId: doc.id,
              name: ext ? `${displayName}.${ext}` : displayName,
              type: doc.docType,
              size: doc.size,
              mimeType: doc.mimeType,
              fileData: null, // uploaded via presigned path; avoid mega JSON submit
              file: doc.file || null,
              uploadStatus: doc.uploadStatus || (doc.restoredFromServer ? 'success' : 'idle'),
              restoredFromServer: doc.restoredFromServer,
              uploadedAt: doc.uploadedAt,
              petName: doc.petName,
              petIndex: doc.petIndex,
              petNumber: doc.petNumber,
            };
          });
          await handleDocumentUploadComplete(documentsForBackend);
          return;
        }
        break;
      }
      case 'attestation':
        isValid = attestationAcknowledged;
        break;
      default:
        isValid = true;
    }

    if (!isValid) {
      // Validation failed — bring the first invalid field into view and focus it
      // so the user understands why Next didn't advance.
      scrollToFirstInvalidField();
      return;
    }

    if (isValid && step < totalSteps) {
      // Skip documents step for Service Animals
      const currentPet = formData.pets[currentPetIndex] || formData.pets[0];
      // Ensure a draft exists as soon as the user advances past early steps
      // so photo/document picks can upload immediately.
      if (step >= 1 && !activeDraftId) {
        ensureDraftPet().catch((err) => console.warn('Early draft create failed:', err));
      }
      if (stepInfo.type === 'vetInfo' && currentPet.animalType === 'sa') {
        setStep(step + 2); // Skip documents, go to attestation
      } else {
        setStep(step + 1);
      }
    } else if (isValid && step === totalSteps && stepInfo.type === 'attestation') {
      // Submit from Review & Attestation step with attestation data
      const currentPetForUpload = formData.pets[currentPetIndex] || formData.pets[0];
      const photoRequired = isFieldVisible('q1') && isFieldRequired('q1');
      const photoStatus =
        currentPetForUpload?.photos?.[0]?.uploadStatus ||
        currentPetForUpload?.photoUploadStatus ||
        photoUploadStatus;
      const docs = getCurrentPetDocuments();
      const failedDocs = docs.filter(
        (d) => d.file && !d.restoredFromServer && d.uploadStatus === 'failed',
      );
      const uploadingDocs = docs.filter((d) => d.uploadStatus === 'uploading');
      if (photoRequired && photoStatus === 'failed') {
        setUploadBlockMessage('Pet photo did not upload. Retry the photo upload before submitting.');
        return;
      }
      if (photoRequired && (photoStatus === 'uploading' || photoStatus === 'idle') && currentPetForUpload?.photos?.[0]?.file) {
        setUploadBlockMessage('Pet photo is still uploading. Please wait.');
        return;
      }
      if (failedDocs.length > 0 || uploadingDocs.length > 0) {
        setUploadBlockMessage(
          uploadingDocs.length > 0
            ? 'Documents are still uploading. Please wait.'
            : 'One or more documents failed to upload. Retry them before submitting.',
        );
        return;
      }
      setUploadBlockMessage('');

      const signature = formData.contactEmail
        ? `${formData.contactEmail}`
        : undefined;
      const attestation: AttestationData = buildAttestationData(
        PET_REGISTRATION_ATTESTATION_TEXT,
        signature
      );

      if (isMultiPetWorkflow && typeof onPetComplete === 'function') {
        const currentPet = formData.pets[currentPetIndex] || formData.pets[0];
        const petPayload = {
          property: formData.property,
          unit: formData.unit,
          contactEmail: formData.contactEmail,
          contactPhone: formData.contactPhone,
          petName: currentPet.petName,
          petType: currentPet.petType === 'other' ? (currentPet.customPetType || 'other') : currentPet.petType,
          breed: currentPet.breed === 'Other' ? (currentPet.customBreed || 'Other') : currentPet.breed,
          age: currentPet.age,
          weight: currentPet.weight,
          gender: currentPet.gender,
          move_in_date: currentPet.move_in_date || parseDateToYMD(gcResidentInfo?.move_in),
          move_out_date: currentPet.move_out_date || parseDateToYMD(gcResidentInfo?.expected_move_out),
          spayed_neutered: currentPet.spayed_neutered,
          // Convert "Yes"/"No" string to boolean for database storage
          previous_pet_ownership: currentPet.previousPetOwnership === 'Yes' ? true : currentPet.previousPetOwnership === 'No' ? false : null,
          color: currentPet.color === 'Other' ? (currentPet.customColor || 'Other') : currentPet.color,
          markings: currentPet.markings === 'Other' ? (currentPet.customMarkings || 'Other') : currentPet.markings,
          animalType: currentPet.animalType,
          vetClinic: currentPet.vetClinic,
          vetPhone: currentPet.vetPhone,
          vetAddress: currentPet.vetAddress,
          lastVisit: currentPet.lastVisit,
          esaProfessional: currentPet.esaProfessional,
          esaLetterDate: currentPet.esaLetterDate,
          esaProviderLicenseNumber: currentPet.esaProviderLicenseNumber,
          esaProviderLicenseState: currentPet.esaProviderLicenseState,
          documents: currentPet.documents || [],
          photos: currentPet.photos || [],
          photoUploadStatus: currentPet.photoUploadStatus || photoUploadStatus,
          customFields: currentPet.customFields || {},
          attestation,
        };
        setIsSubmitting(true);
        try {
          await onPetComplete(petPayload, stepInfo.petIndex);
          setUploadStates(initialUploadStates);
        } finally {
          setIsSubmitting(false);
        }
      } else if (typeof onComplete === 'function') {
        let draftId: number | null = null;
        try {
          // Always verify/create — do not trust cached activeDraftId without a status check.
          draftId = await ensureDraftPet();
        } catch (err) {
          console.error('Failed to create/verify draft before finalize:', err);
        }
        if (!draftId) {
          setUploadBlockMessage('Could not prepare your registration draft. Please try again.');
          return;
        }
        const finalFormData = {
          ...formData,
          attestation,
          ...(draftId ? { draftPetId: draftId } : {}),
          pets: formData.pets.map((pet, index) => ({
            ...pet,
            petType: pet.petType === 'other' ? (pet.customPetType || 'other') : pet.petType,
            breed: pet.breed === 'Other' ? (pet.customBreed || 'Other') : pet.breed,
            color: pet.color === 'Other' ? (pet.customColor || 'Other') : pet.color,
            markings: pet.markings === 'Other' ? (pet.customMarkings || 'Other') : pet.markings,
            documents: pet.documents || [],
            previous_pet_ownership: pet.previousPetOwnership === 'Yes' ? true : pet.previousPetOwnership === 'No' ? false : null,
            photoUploadStatus: pet.photoUploadStatus || (index === currentPetIndex ? photoUploadStatus : pet.photoUploadStatus),
          })),
        };
        setIsSubmitting(true);
        try {
          await onComplete(finalFormData);
        } finally {
          setIsSubmitting(false);
        }
      }
    }
  };

  const handlePrevious = () => {
    if (step > 1) {
      // Skip documents step when going back for Service Animals
      const currentPet = formData.pets[currentPetIndex] || formData.pets[0];
      const stepInfo = getCurrentStepInfo();
      if (stepInfo.type === 'attestation' && currentPet.animalType === 'sa') {
        setStep(step - 2); // Skip documents, go back to vet info
      } else {
        setStep(step - 1);
      }
    } else if (step === 1 && isMultiPetWorkflow) {
      onClose();
    }
  };

  const getPetDocumentKey = () => {
    return `pet_${currentPetIndex}`;
  };

  const getCurrentPetDocuments = () => {
    const key = getPetDocumentKey();
    return petDocuments[key] || EMPTY_DOCS;
  };

  const updateCurrentPetDocuments = (documents) => {
    const key = getPetDocumentKey();
    setPetDocuments(prev => ({
      ...prev,
      [key]: documents
    }));
  };

  const handleDocumentUploadComplete = async (documentsData) => {
    console.log('📄 Documents received from DocumentUploadView:', documentsData);
    
    // Only include new documents (with fileData) — restored docs are already in S3
    const draftId = draftPet?.id || draftPet?.pet_id || null;
    const updatedDocuments = documentsData
      .filter(doc => doc.fileData)
      .map(doc => ({
        ...doc,
        tempId: doc.tempId || `temp_${currentPetIndex}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        petId: draftId,
        petIndex: currentPetIndex,
        petName: formData.pets[currentPetIndex]?.petName || `Pet ${currentPetIndex + 1}`,
        petNumber: currentPetIndex + 1,
        uploadedAt: doc.uploadedAt || new Date().toISOString(),
      }));

    console.log('📋 Processed documents for backend:', updatedDocuments);

    setFormData(prev => ({
      ...prev,
      pets: prev.pets.map((pet, index) =>
        index === currentPetIndex 
          ? { ...pet, documents: updatedDocuments }
          : pet
      )
    }));

    // Advance to Step 6 (Review & Attestation); submit happens there after attestation is acknowledged
    setStep(step + 1);
  };

  const getSelectedPropertyAddress = () => {
    const selectedProperty = properties.find(p => p.name === formData.property);
    return selectedProperty?.address || '';
  };

  const getCurrentPhoneConfig = () => {
    return countryPhoneConfigs.find(c => c.code === selectedCountryCode) || countryPhoneConfigs[0];
  };

  const DocumentUploadSection = () => {
    const currentPet = formData.pets[currentPetIndex] || formData.pets[0];

    const FileUploadField = ({ type, label, required = false, fileName, status, onFileSelect }) => {
      const getStatusIcon = () => {
        switch (status) {
          case 'uploading':
            return (
              <div className="w-4 h-4 border-2 border-[color:var(--pmc-primary)] border-t-transparent rounded-full animate-spin" />
            );
          case 'success':
            return <CheckCircle className="w-4 h-4 text-green-500" />;
          default:
            return <Upload className="w-4 h-4 text-gray-400" />;
        }
      };

      return (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-900">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
          <div className={`relative border rounded-lg p-3 transition-colors ${
            status === 'success' ? 'border-green-300 bg-green-50' :
            status === 'uploading' ? 'border-blue-300 bg-blue-50' :
            'border-gray-300 hover:border-gray-400'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {getStatusIcon()}
                <span className={`text-sm ${
                  fileName ? 'text-gray-900' : 'text-gray-500'
                }`}>
                  {fileName || (
                    required ? `${label.toLowerCase()} document` : `${label} (optional)`
                  )}
                </span>
              </div>
              {status !== 'success' && (
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      onFileSelect(file);
                    }
                    e.target.value = '';
                  }}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              )}
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <Upload className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Documents</h3>
              <p className="text-xs sm:text-sm text-gray-600">Please upload the following documentation</p>
            </div>
          </div>
          <button
            onClick={handleCloseAttempt}
            className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-blue-900 mb-2">Important Information</h4>
              <ul className="text-xs sm:text-sm text-blue-800 space-y-1">
                <li>• All documents must be current and not expired</li>
                <li>• Insurance policy must cover liability for pet-related damages</li>
                <li>• Documents will be reviewed within 2-3 business days</li>
                <li>• Files will only be uploaded after clicking "Complete Upload"</li>
                <li>• Make sure all documents are specifically for {currentPet.petName || 'your pet'}</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:gap-6">
          <FileUploadField
            type="petInsurance"
            label="Pet Insurance"
            required
            fileName={uploadStates.petInsurance.file?.name}
            status={uploadStates.petInsurance.status}
            onFileSelect={(file) => handleFileUpload('petInsurance', file)}
          />
          
          <FileUploadField
            type="vaccination"
            label="Vaccination Records"
            required
            fileName={uploadStates.vaccination.file?.name}
            status={uploadStates.vaccination.status}
            onFileSelect={(file) => handleFileUpload('vaccination', file)}
          />
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-6 border-t border-gray-200">
          <button
            onClick={handlePrevious}
            className="w-full sm:w-auto px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            BACK
          </button>
          
          <button
            onClick={() => {
              const requiredUploaded = uploadStates.petInsurance.status === 'success' && 
                                     uploadStates.vaccination.status === 'success';
                          
              if (requiredUploaded) {
                handleDocumentUploadComplete([]);
              } else {
                alert('Please upload all required documents.');
              }
            }}
            className="w-full sm:w-auto px-6 py-2 bg-[color:var(--pmc-primary)] text-white rounded-lg font-bold hover:shadow-lg transition-all"
          >
            DONE
          </button>
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    const stepInfo = getCurrentStepInfo();
    const currentPet = formData.pets[currentPetIndex] || formData.pets[0];

    switch (stepInfo.type) {
      case 'property':
        const currentPhoneConfig = getCurrentPhoneConfig();
        if (isStep1Loading) {
          return (
            <div className="flex items-center justify-center py-16">
              <LoadingSpinner size="medium" text="Loading your resident information..." />
            </div>
          );
        }
        return (
          <div className="space-y-4">
            {/* Lease Details Section - at the top */}
            {gcResidentInfo && (
              <>
                <div className="flex items-center gap-3 mb-2">
                  <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
                  <p className="text-xs sm:text-sm text-gray-600">Lease Details</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Resident Full Name
                    </label>
                    <div className="w-full p-3 border border-gray-300 bg-gray-50 rounded-lg text-gray-700">
                      {gcResidentInfo.resident_full_name || 'Not specified'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Building
                    </label>
                    <div className="w-full p-3 border border-gray-300 bg-gray-50 rounded-lg text-gray-700">
                      {gcResidentInfo.building || 'Not specified'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Move-In Date
                    </label>
                    <div className="w-full p-3 border border-gray-300 bg-gray-50 rounded-lg text-gray-700">
                      {gcResidentInfo.move_in ? formatDateMMDDYYYY(parseDateToYMD(gcResidentInfo.move_in)) : 'Not specified'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Move-Out Date
                    </label>
                    <div className="w-full p-3 border border-gray-300 bg-gray-50 rounded-lg text-gray-700">
                      {gcResidentInfo.expected_move_out ? formatDateMMDDYYYY(parseDateToYMD(gcResidentInfo.expected_move_out)) : 'Not specified'}
                    </div>
                  </div>
                </div>

                <div className="border-b border-gray-200 pb-1 mb-1" />
              </>
            )}

            <div className="flex items-center gap-3 mb-4">
              <Building className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
              <p className="text-xs sm:text-sm text-gray-600">Where will this {getAnimalTypeLabelLower()} be living?</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Property <span className="text-red-500">*</span>
              </label>
              {(gcResidentInfo || userProperty) ? (
                // Read-only display when data is from Get Covered or user already has assigned property
                <div className="w-full p-3 border border-gray-300 bg-gray-50 rounded-lg text-gray-700">
                  {formData.property || 'Not specified'}
                </div>
              ) : (
                // Editable select when no Get Covered data and no assigned property
                <select
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                    validationErrors.property ? 'border-red-500' : 'border-gray-300'
                  }`}
                  value={formData.property}
                  onChange={(e) => handleInputChange('property', e.target.value)}
                  required
                  disabled={loadingProperties}
                >
                  <option value="">
                    {loadingProperties ? 'Loading properties...' : 'Select'}
                  </option>
                  {properties.map(property => (
                    <option key={property.id} value={property.name}>
                      {property.name}
                    </option>
                  ))}
                </select>
              )}
              {validationErrors.property && (
                <p className="text-xs text-red-600 mt-1 animate-pulse">
                  {validationErrors.property}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Unit Number <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              {gcResidentInfo && prefilledLeaseValues.unit.trim() ? (
                // Read-only once a value exists on the lease (either GC-provided
                // or resident-entered and saved to users.unit on a prior visit).
                <div className="w-full p-3 border border-gray-300 bg-gray-50 rounded-lg text-gray-700">
                  {formData.unit || 'Not specified'}
                </div>
              ) : (
                // Editable when Get Covered has no record, or has a record but no unit on file yet
                <input
                  type="text"
                  placeholder="e.g. 204"
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                    validationErrors.unit ? 'border-red-500' : 'border-gray-300'
                  }`}
                  value={formData.unit}
                  onChange={(e) => handleInputChange('unit', e.target.value)}
                />
              )}
              {validationErrors.unit && (
                <p className="text-xs text-red-600 mt-1 animate-pulse">
                  {validationErrors.unit}
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Contact Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                placeholder="your@email.com"
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                  validationErrors.contactEmail ? 'border-red-500' : 'border-gray-300'
                } ${(gcResidentInfo || (formData.contactEmail && formData.contactEmail.includes('@getcovered.io'))) ? 'bg-gray-50' : ''}`}
                value={formData.contactEmail}
                onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                required
                readOnly={!!gcResidentInfo || (formData.contactEmail && formData.contactEmail.includes('@getcovered.io'))}
              />
              {validationErrors.contactEmail && (
                <p className="text-xs text-red-600 mt-1 animate-pulse">
                  {validationErrors.contactEmail}
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Phone Number <span className="text-gray-500 font-normal">(optional)</span>
              </label>

              {gcResidentInfo && prefilledLeaseValues.phone.trim() ? (
                // Read-only once a value exists (either GC-provided or
                // resident-entered and saved to users.contact_phone on a prior visit).
                <div className="w-full p-3 border border-gray-300 bg-gray-50 rounded-lg text-gray-700">
                  {formData.contactPhone || 'Not specified'}
                </div>
              ) : (
                // Editable when Get Covered has no record, or has a record but no phone on file yet
                <div className="flex flex-col sm:flex-row gap-2 min-w-0">
                  <div className="relative w-full sm:w-auto flex-shrink-0">
                    <select
                      className="w-full sm:w-auto appearance-none p-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors cursor-pointer min-w-0"
                      value={selectedCountryCode}
                      onChange={(e) => handleCountryCodeChange(e.target.value)}
                    >
                      {countryPhoneConfigs.map(config => (
                        <option key={config.code} value={config.code}>
                          {config.flag} {config.code}
                        </option>
                      ))}
                    </select>
                    <Globe className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                  
                  <div className="flex-1 relative min-w-0">
                    <input
                      type="tel"
                      placeholder={`Enter ${currentPhoneConfig.maxDigits} digits`}
                      className={`w-full p-3 pr-16 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                        formData.phoneNumber.length === currentPhoneConfig.maxDigits ? 'border-green-500 bg-green-50' :
                        'border-gray-300'
                      }`}
                      value={formData.phoneNumber}
                      onChange={(e) => handlePhoneNumberChange(e.target.value)}
                      maxLength={currentPhoneConfig.maxDigits}
                    />
                    <span className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-xs ${
                      formData.phoneNumber.length === currentPhoneConfig.maxDigits ? 'text-green-600' :
                      formData.phoneNumber.length >= currentPhoneConfig.maxDigits * 0.8 ? 'text-orange-500' :
                      'text-gray-400'
                    }`}>
                      {formData.phoneNumber.length}/{currentPhoneConfig.maxDigits}
                    </span>
                  </div>
                </div>
              )}
            </div>

          </div>
        );

      case 'petDetails': {
        const petDetailFieldIds = ['q1','q2','q3','q4','q5','q6','q7','q8','q11','q12','q19'];
        const hasAnyVisibleField = petDetailFieldIds.some(id => isFieldVisible(id));
        const hasCustomAnimalInfoFields = getCustomQuestions('animal_info').length > 0;

        if (!hasAnyVisibleField && !hasCustomAnimalInfoFields) {
          return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Info className="w-10 h-10 text-gray-400 mb-3" />
              <p className="text-sm text-gray-600">
                This property does not require additional pet information. Please proceed to the next step.
              </p>
            </div>
          );
        }

        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* q1: Pet Photo */}
            {isFieldVisible('q1') && (
            <div className="md:col-span-2" style={{ order: getQuestionOrder('q1') }}>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {getFieldLabel('q1', 'Pet Photo')} {isFieldRequired('q1') ? <span className="text-red-500">*</span> : '(optional)'}
              </label>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <input
                  ref={petPhotoInputRef}
                  id="pet-photo-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files && e.target.files[0];
                    if (!file) return;

                    if (currentPet.photos[0]?.preview) {
                      URL.revokeObjectURL(currentPet.photos[0].preview);
                    }

                    const preview = URL.createObjectURL(file);
                    handleInputChange('pet.photos', [{
                      file,
                      preview,
                      type: 'main',
                      label: 'Pet Photo',
                      uploadStatus: 'uploading',
                    }]);
                    setPhotoUploadStatus('uploading');
                    e.target.value = '';

                    try {
                      const draftId = await ensureDraftPet();
                      if (!draftId) {
                        setPhotoUploadStatus('failed');
                        handleInputChange('pet.photos', [{
                          file,
                          preview,
                          type: 'main',
                          label: 'Pet Photo',
                          uploadStatus: 'failed',
                        }]);
                        return;
                      }
                      const result = await uploadFileToPet({
                        petId: draftId,
                        file,
                        uploadType: 'pet_photo',
                      });
                      const status = result.success ? 'success' : 'failed';
                      setPhotoUploadStatus(status);
                      handleInputChange('pet.photos', [{
                        file,
                        preview,
                        type: 'main',
                        label: 'Pet Photo',
                        uploadStatus: status,
                      }]);
                      setFormData((prev) => ({
                        ...prev,
                        pets: prev.pets.map((p, idx) =>
                          idx === currentPetIndex
                            ? { ...p, photoUploadStatus: status }
                            : p,
                        ),
                      }));
                    } catch (err) {
                      console.error('Pet photo upload failed:', err);
                      setPhotoUploadStatus('failed');
                      handleInputChange('pet.photos', [{
                        file,
                        preview,
                        type: 'main',
                        label: 'Pet Photo',
                        uploadStatus: 'failed',
                      }]);
                    }
                  }}
                />
                <div className="flex-shrink-0">
                  {currentPet.photos[0]?.preview ? (
                    <button
                      type="button"
                      onClick={openPetPhotoPicker}
                      title="Tap to change photo"
                      aria-label="Change pet photo"
                      className={`block w-28 h-28 rounded-lg border overflow-hidden bg-gray-50 cursor-pointer ${
                        validationErrors.photos ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <img src={currentPet.photos[0].preview} alt="Pet photo preview" className="w-full h-full object-cover" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={openPetPhotoPicker}
                      title="Tap to add a photo"
                      aria-label="Add pet photo"
                      className={`w-28 h-28 rounded-lg border border-dashed flex flex-col items-center justify-center gap-1.5 cursor-pointer text-gray-400 hover:border-purple-400 hover:text-purple-500 hover:bg-purple-50 transition-colors ${
                        validationErrors.photos ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-xs font-medium">Tap to add photo</span>
                    </button>
                  )}
                </div>
                <div className="flex flex-col items-center gap-2">
                  <button
                    type="button"
                    onClick={openPetPhotoPicker}
                    className="inline-flex items-center shadow-[0px_1px_2px_rgba(0,0,0,0.05)] rounded-[1000px] bg-white border border-[#b9bbc6] h-9 overflow-hidden px-[12px] py-2 text-[#1e1f24] hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <span className="text-xs uppercase font-medium">{currentPet.photos[0]?.preview ? 'Change Photo' : 'Upload Photo'}</span>
                  </button>
                  {(currentPet.photos[0]?.file || currentPet.photos[0]?.preview) && (
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setShowPhotoPreview(true)}
                        className="text-xs text-blue-600 hover:text-blue-700"
                      >
                        Preview
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          const preview = currentPet.photos[0]?.preview;
                          if (preview?.startsWith('blob:')) {
                            URL.revokeObjectURL(preview);
                          } else if (preview) {
                            const petId = draftPet?.id || draftPet?.pet_id;
                            if (petId) {
                              try {
                                await petService.deletePetImage(Number(petId));
                              } catch (err) {
                                console.warn('Failed to delete image from server:', err);
                              }
                            }
                          }
                          handleInputChange('pet.photos', [{ file: null, preview: null, type: 'main', label: 'Pet Photo' }]);
                        }}
                        className="text-xs text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  )}

                  {showPhotoPreview && currentPet.photos[0]?.preview && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={() => setShowPhotoPreview(false)}>
                      <div className="relative bg-white rounded-lg shadow-xl p-3 max-w-md max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => setShowPhotoPreview(false)}
                          className="absolute -top-2 -right-2 w-7 h-7 flex items-center justify-center rounded-full bg-white shadow-md text-gray-500 hover:text-gray-700 text-sm font-bold"
                        >
                          ✕
                        </button>
                        <img
                          src={currentPet.photos[0].preview}
                          alt="Pet photo preview"
                          className="max-w-full max-h-[70vh] rounded object-contain"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-500">
                Upload a clear, full body photo of your pet showing their face and body.
              </div>
              {validationErrors.photos && (
                <p className="text-xs text-red-600 mt-1 animate-pulse">
                  {validationErrors.photos}
                </p>
              )}
              {photoUploadStatus === 'uploading' && (
                <p className="text-xs text-blue-600 mt-1">Uploading photo…</p>
              )}
              {photoUploadStatus === 'success' && (
                <p className="text-xs text-green-600 mt-1">Photo uploaded</p>
              )}
              {photoUploadStatus === 'failed' && (
                <p className="text-xs text-red-600 mt-1">
                  Photo didn&apos;t upload — tap Upload Photo to retry. Your other answers are saved.
                </p>
              )}
            </div>
            )}

            {/* q2: Pet Type / Animal Type */}
            {isFieldVisible('q2') && (
            <div className="md:col-span-2" style={{ order: getQuestionOrder('q2') }}>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {getFieldLabel('q2', 'Pet Type')} {isFieldRequired('q2') && <span className="text-red-500">*</span>}
              </label>

              <div className={`grid ${currentPet.animalType === 'sa' ? 'grid-cols-2' : 'grid-cols-3'} gap-2 sm:gap-3`}>
                {[
                  { value: 'dog', label: 'Dog', icon: DogIcon },
                  ...(currentPet.animalType === 'sa' ? [{ value: 'miniature_horse', label: 'Miniature Horse', icon: HorseIcon }] : []),
                  ...(currentPet.animalType !== 'sa' ? [{ value: 'cat', label: 'Cat', icon: CatIcon }] : []),
                  ...(currentPet.animalType !== 'sa' ? [{ value: 'other', label: 'Other', icon: PawIcon }] : []),
                ].map(type => {
                  const Icon = type.icon;
                  const isRestricted = !isPetTypeAllowed(type.value);
                  const isSelected = currentPet.petType === type.value;
                  return (
                    <button
                      key={type.value}
                      onClick={() => handleInputChange('pet.petType', type.value)}
                      className={`flex-1 p-2 sm:p-3 border rounded-lg flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 transition-all ${
                        isSelected && isRestricted
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : isSelected
                          ? 'border-purple-600 bg-purple-50 text-purple-700'
                          : isRestricted
                          ? 'border-gray-300 hover:bg-gray-50 text-gray-400'
                          : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      <Icon />
                      <span className="text-xs sm:text-sm">{type.label}</span>
                      {isRestricted && <span className="text-[10px] text-red-500 font-medium">Restricted</span>}
                    </button>
                  );
                })}
              </div>
              {validationErrors.petType && (
                <p className="text-xs text-red-600 mt-1 animate-pulse">{validationErrors.petType}</p>
              )}
              {currentPet.petType && !isPetTypeAllowed(currentPet.petType) && (
                <p className="text-xs text-red-500 mt-2">This pet type is restricted at this property</p>
              )}
              {currentPet.petType === 'other' && (
                <div className="mt-3 pet-type-autocomplete-container relative">
                  <input
                    type="text"
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                      validationErrors.customPetType ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Search or type pet type (e.g. Bird, Rabbit, Turtle...)"
                    value={currentPet.customPetType || ''}
                    onChange={(e) => handlePetTypeSearch(e.target.value)}
                    onFocus={() => {
                      if (currentPet.customPetType?.trim()) {
                        handlePetTypeSearch(currentPet.customPetType);
                      } else {
                        setPetTypeSuggestions(otherPetTypes);
                        setShowPetTypeSuggestions(true);
                      }
                    }}
                  />
                  {showPetTypeSuggestions && petTypeSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {petTypeSuggestions.map((type, index) => (
                        <button
                          key={index}
                          type="button"
                          className="w-full text-left px-4 py-2 hover:bg-purple-50 text-sm text-gray-700 transition-colors border-b border-gray-50 last:border-b-0"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handlePetTypeSelection(type)}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  )}
                  {validationErrors.customPetType && (
                    <p className="text-xs text-red-600 mt-1 animate-pulse">
                      {validationErrors.customPetType}
                    </p>
                  )}
                </div>
              )}
            </div>
            )}

            {/* q3: Animal Name */}
            {isFieldVisible('q3') && (
            <div style={{ order: getQuestionOrder('q3') }}>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {getFieldLabel('q3', `${getAnimalTypeLabel()}'s Name`)} {isFieldRequired('q3') && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                placeholder={`Enter your ${currentPet.animalType === 'sa' ? "service animal's" : currentPet.animalType === 'esa' ? "emotional support animal's" : "pet's"} name`}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                  petNameValidationError ? 'border-red-500' : 'border-gray-300'
                }`}
                value={currentPet.petName}
                onChange={(e) => handleCurrentPetNameChange(e.target.value)}
                onBlur={validateCurrentPetName}
                maxLength={20}
                required
              />
              {petNameValidationError && (
                <p className="text-xs text-red-600 mt-1 animate-pulse">
                  {petNameValidationError}
                </p>
              )}
            </div>
            )}

            {/* q4: Breed */}
            {isFieldVisible('q4') && (
            <div style={{ order: getQuestionOrder('q4') }}>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {getFieldLabel('q4', 'Breed')} {isFieldRequired('q4') && <span className="text-red-500">*</span>}
              </label>

              {currentPet.petType === 'other' ? (
                <input
                  type="text"
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                    validationErrors.breed ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter breed name"
                  value={currentPet.breed || ''}
                  onChange={(e) => handleInputChange('pet.breed', e.target.value)}
                  required
                />
              ) : currentPet.breed === 'Other' ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                      validationErrors.breed ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter breed name"
                    value={currentPet.customBreed || ''}
                    onChange={(e) => handleInputChange('pet.customBreed', e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => {
                      handleInputChange('pet.breed', '');
                      handleInputChange('pet.customBreed', '');
                    }}
                    className="text-xs text-purple-600 hover:text-purple-700"
                  >
                    &larr; Back to list
                  </button>
                </div>
              ) : (
                <select
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                    validationErrors.breed ? 'border-red-500' :
                    (currentPet.breed && !isBreedAllowed(currentPet.breed)) ? 'border-red-500' :
                    'border-gray-300'
                  }`}
                  value={currentPet.breed}
                  onChange={(e) => handleInputChange('pet.breed', e.target.value)}
                  required
                >
                  <option value="">Select breed</option>
                  {(currentPet.petType === 'dog' ? dogBreeds : currentPet.petType === 'miniature_horse' ? miniatureHorseBreeds : catBreeds).map(breed => (
                    <option key={breed} value={breed}>
                      {breed}{!isBreedAllowed(breed) ? ' (Restricted)' : ''}
                    </option>
                  ))}
                </select>
              )}
              {currentPet.breed && !isBreedAllowed(currentPet.breed) && (
                <p className="text-xs text-red-500 mt-1">This breed is restricted at this property</p>
              )}
              {validationErrors.breed && (
                <p className="text-xs text-red-600 mt-1 animate-pulse">
                  {validationErrors.breed}
                </p>
              )}
            </div>
            )}

            {/* q5: Weight */}
            {isFieldVisible('q5') && (
            <div style={{ order: getQuestionOrder('q5') }}>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  {getFieldLabel('q5', 'Weight')} (lbs) {isFieldRequired('q5') && <span className="text-red-500">*</span>}
                </label>

                <input
                  type="number"
                  placeholder="Weight in lbs"
                  min="0"
                  step="0.01"
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                    validationErrors.weight ? 'border-red-500' :
                    (currentPet.weight?.value && !isWeightAllowed(currentPet.weight.value)) ? 'border-orange-500' :
                    'border-gray-300'
                  }`}
                  value={currentPet.weight?.value || ''}
                  onChange={(e) => {
                    let value = e.target.value;
                    if (value.includes('.')) {
                      const parts = value.split('.');
                      if (parts[1] && parts[1].length > 2) {
                        value = parts[0] + '.' + parts[1].substring(0, 2);
                      }
                    }
                    // Always set unit to 'lbs' — no hard cap, allow any value
                    handleInputChange('pet.weight', { value: value, unit: 'lbs' });
                  }}
                  required
                />
                {validationErrors.weight && (
                  <p className="text-xs text-red-600 mt-1 animate-pulse">
                    {validationErrors.weight}
                  </p>
                )}
                {currentPet.weight?.value && !isWeightAllowed(currentPet.weight.value) && (
                  <p className="text-xs text-orange-600 mt-1">
                    This exceeds the property's weight limit
                  </p>
                )}
              </div>
            )}

            {/* q6: Age */}
            {isFieldVisible('q6') && (
            <div style={{ order: getQuestionOrder('q6') }}>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {getFieldLabel('q6', 'Age')} (year) {isFieldRequired('q6') && <span className="text-red-500">*</span>}
              </label>
              <select
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                  validationErrors.age ? 'border-red-500' : 'border-gray-300'
                }`}
                value={currentPet.age !== null && currentPet.age !== undefined ? currentPet.age : ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? null : parseInt(e.target.value);
                  handleInputChange('pet.age', value);
                }}
                required
              >
                <option value="">Select age</option>
                <option value="0">Less than 1 year</option>
                {Array.from({ length: 25 }, (_, i) => i + 1).map(year => (
                  <option key={year} value={year}>
                    {year} {year === 1 ? 'year' : 'years'}
                  </option>
                ))}
              </select>
              {validationErrors.age && (
                <p className="text-xs text-red-600 mt-1 animate-pulse">
                  {validationErrors.age}
                </p>
              )}
            </div>
            )}

            {/* q7: Gender */}
            {isFieldVisible('q7') && (
            <div style={{ order: getQuestionOrder('q7') }}>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {getFieldLabel('q7', 'Gender')} {isFieldRequired('q7') && <span className="text-red-500">*</span>}
              </label>
              <select
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                  validationErrors.gender ? 'border-red-500' : 'border-gray-300'
                }`}
                value={currentPet.gender || ''}
                onChange={(e) => handleInputChange('pet.gender', e.target.value)}
                required
              >
                <option value="">Select gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
              {validationErrors.gender && (
              <p className="text-xs text-red-600 mt-1 animate-pulse">
                {validationErrors.gender}
              </p>
              )}
            </div>
            )}

            {/* q8: Spayed/Neutered */}
            {isFieldVisible('q8') && (
            <div style={{ order: getQuestionOrder('q8') }}>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {getFieldLabel('q8', 'Spayed/Neutered')} {isFieldRequired('q8') && <span className="text-red-500">*</span>}
              </label>
              <select
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                  validationErrors.spayed_neutered ? 'border-red-500' : 'border-gray-300'
                }`}
                value={currentPet.spayed_neutered === null ? '' : currentPet.spayed_neutered ? 'yes' : 'no'}
                onChange={(e) => {
                  const value = e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null;
                  handleInputChange('pet.spayed_neutered', value);
                }}
                required
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
              {validationErrors.spayed_neutered && (
              <p className="text-xs text-red-600 mt-1 animate-pulse">
                {validationErrors.spayed_neutered}
              </p>
              )}
            </div>
            )}

            {/* q11: Primary Color */}
            {isFieldVisible('q11') && (
            <div style={{ order: getQuestionOrder('q11') }}>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {getFieldLabel('q11', 'Primary Color')} {isFieldRequired('q11') && <span className="text-red-500">*</span>}
              </label>
              {currentPet.color === 'Other' ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                      validationErrors.color ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter color"
                    value={currentPet.customColor || ''}
                    onChange={(e) => handleInputChange('pet.customColor', e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => {
                      handleInputChange('pet.color', '');
                      handleInputChange('pet.customColor', '');
                    }}
                    className="text-xs text-purple-600 hover:text-purple-700"
                  >
                    &larr; Back to list
                  </button>
                </div>
              ) : (
                <select
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                    validationErrors.color ? 'border-red-500' : 'border-gray-300'
                  }`}
                  value={currentPet.color}
                  onChange={(e) => handleInputChange('pet.color', e.target.value)}
                  required
                >
                  <option value="">Select primary color</option>
                  {petColors.map(color => (
                    <option key={color} value={color}>
                      {color}
                    </option>
                  ))}
                </select>
              )}
              {validationErrors.color && (
                <p className="text-xs text-red-600 mt-1 animate-pulse">
                  {validationErrors.color}
                </p>
              )}
            </div>
            )}

            {/* q12: Markings/Patterns */}
            {isFieldVisible('q12') && (
            <div style={{ order: getQuestionOrder('q12') }}>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {getFieldLabel('q12', 'Markings/Patterns')} {!isFieldRequired('q12') && '(Optional)'} {isFieldRequired('q12') && <span className="text-red-500">*</span>}
              </label>
              {currentPet.markings === 'Other' ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                      validationErrors.markings ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter markings/pattern"
                    value={currentPet.customMarkings || ''}
                    onChange={(e) => handleInputChange('pet.customMarkings', e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      handleInputChange('pet.markings', '');
                      handleInputChange('pet.customMarkings', '');
                    }}
                    className="text-xs text-purple-600 hover:text-purple-700"
                  >
                    &larr; Back to list
                  </button>
                </div>
              ) : (
                <select
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                    validationErrors.markings ? 'border-red-500' : 'border-gray-300'
                  }`}
                  value={currentPet.markings || ''}
                  onChange={(e) => handleInputChange('pet.markings', e.target.value)}
                >
                  <option value="">Select markings</option>
                  {petMarkings.map(marking => (
                    <option key={marking} value={marking}>
                      {marking}
                    </option>
                  ))}
                </select>
              )}
              {validationErrors.markings && (
                <p className="text-xs text-red-600 mt-1 animate-pulse">
                  {validationErrors.markings}
                </p>
              )}
            </div>
            )}

            {/* q19: Previous Pet Ownership History */}
            {isFieldVisible('q19') && (
            <div className="md:col-span-2" style={{ order: getQuestionOrder('q19') }}>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {getFieldLabel('q19', 'Do you have a Previous Pet Ownership History?')} {isFieldRequired('q19') && <span className="text-red-500">*</span>}
              </label>
              <select
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                  validationErrors.previousPetOwnership ? 'border-red-500' : 'border-gray-300'
                }`}
                value={currentPet.previousPetOwnership || ''}
                onChange={(e) => handleInputChange('pet.previousPetOwnership', e.target.value)}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {validationErrors.previousPetOwnership && (
                <p className="text-xs text-red-600 mt-1 animate-pulse">
                  {validationErrors.previousPetOwnership}
                </p>
              )}
            </div>
            )}

            {/* Custom admin-added questions for animal_info */}
            {renderCustomFields('animal_info')}
          </div>
        );
      }

      case 'esaInfo':
        return (
          <div className="space-y-4">
            <p className="text-xs sm:text-sm text-gray-600 mb-4">Select the type of animal you're registering</p>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-3">
                Animal Type <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2 w-full p-2 border border-gray-300 rounded-lg bg-white">
                {['pet', 'esa', 'sa'].map(type => {
                  // No restrictions on Animal Type step - pet type/breed are selected on the next step
                  const isDisabled = false;
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        if (!isDisabled) {
                          handleInputChange('pet.animalType', type);
                        }
                      }}
                      disabled={isDisabled}
                      className={`flex-1 py-2 px-4 font-medium rounded-md transition-all text-sm sm:text-base ${
                        isDisabled
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                          : (!currentPet.animalType && type === 'pet') || currentPet.animalType === type
                          ? 'bg-purple-900 text-white shadow-md'
                          : 'bg-white text-gray-700'
                      }`}
                    >
                      {type === 'pet' ? 'Pet' : type === 'esa' ? 'Emotional Support Animal' : 'Service Animal'}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-600 mt-2">
                Select the classification that best describes your animal
              </p>
              
              {currentPet.animalType === 'esa' && (
                <>
                  <div className="bg-blue-50 rounded-lg p-4 mt-4">
                    <h4 className="font-medium text-blue-900 mb-2">What is an Emotional Support Animal?</h4>
                    <p className="text-xs sm:text-sm text-blue-800">
                      An Emotional Support Animal provides comfort and support to individuals with emotional or psychological conditions. They are protected under the Fair Housing Act and may be exempt from certain pet fees.
                    </p>
                  </div>

                  {/* Show ESA fee exemption message if enabled by admin */}
                  {questionnaireSettings?.general?.esaExemptFromFees && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-3">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-green-900 mb-1">Fee Exemption</h4>
                          <p className="text-xs sm:text-sm text-green-800">
                            Good news! This property exempts Emotional Support Animals from pet fees and deposits.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {currentPet.animalType === 'sa' && (
                <div className="bg-blue-50 rounded-lg p-4 mt-4">
                  <h4 className="font-medium text-blue-900 mb-2">What is a Service Animal?</h4>
                  <p className="text-xs sm:text-sm text-blue-800">
                    A Service Animal is individually trained to perform specific tasks for a person with a disability. Service animals are protected under the Americans with Disabilities Act (ADA) and are allowed in all public spaces.
                  </p>
                </div>
              )}
            </div>

            {currentPet.animalType === 'esa' && (
              <div className="space-y-4 pt-4 border-t border-gray-200">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs sm:text-sm text-blue-900">
                    <strong>Document Validity:</strong> Emotional Support Animal letters must be less than one year old to be considered valid.
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Health Professional Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Dr. Jane Smith"
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                      validationErrors.esaProfessional ? 'border-red-500' : 'border-gray-300'
                    }`}
                    value={currentPet.esaProfessional}
                    onChange={(e) => handleInputChange('pet.esaProfessional', e.target.value)}
                    required
                  />
                  {validationErrors.esaProfessional && (
                    <p className="text-xs text-red-600 mt-1 animate-pulse">
                      {validationErrors.esaProfessional}
                    </p>
                  )}
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Letter Issue Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      min={getOneYearAgoDate()}
                      max={getTodayDate()}
                      value={currentPet.esaLetterDate}
                      onChange={(e) => handleInputChange('pet.esaLetterDate', e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key !== 'Tab' && e.key !== 'Escape') {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => e.preventDefault()}
                      className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors [&::-webkit-calendar-picker-indicator]:cursor-pointer ${
                        validationErrors.esaLetterDate ? 'border-red-500' : 'border-gray-300'
                      }`}
                      required
                    />
                    {validationErrors.esaLetterDate && (
                      <p className="text-xs text-red-600 mt-1 animate-pulse">
                        {validationErrors.esaLetterDate}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Must be within the last year
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Provider's License Number (Optional)
                    </label>
                    <input
                      type="text"
                      placeholder="Enter license number"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors"
                      value={currentPet.esaProviderLicenseNumber || ''}
                      onChange={(e) => handleInputChange('pet.esaProviderLicenseNumber', e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      State (Optional)
                    </label>
                    <select
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors"
                      value={currentPet.esaProviderLicenseState || ''}
                      onChange={(e) => handleInputChange('pet.esaProviderLicenseState', e.target.value)}
                    >
                      <option value="">Select state</option>
                      <option value="AL">Alabama</option>
                      <option value="AK">Alaska</option>
                      <option value="AZ">Arizona</option>
                      <option value="AR">Arkansas</option>
                      <option value="CA">California</option>
                      <option value="CO">Colorado</option>
                      <option value="CT">Connecticut</option>
                      <option value="DE">Delaware</option>
                      <option value="FL">Florida</option>
                      <option value="GA">Georgia</option>
                      <option value="HI">Hawaii</option>
                      <option value="ID">Idaho</option>
                      <option value="IL">Illinois</option>
                      <option value="IN">Indiana</option>
                      <option value="IA">Iowa</option>
                      <option value="KS">Kansas</option>
                      <option value="KY">Kentucky</option>
                      <option value="LA">Louisiana</option>
                      <option value="ME">Maine</option>
                      <option value="MD">Maryland</option>
                      <option value="MA">Massachusetts</option>
                      <option value="MI">Michigan</option>
                      <option value="MN">Minnesota</option>
                      <option value="MS">Mississippi</option>
                      <option value="MO">Missouri</option>
                      <option value="MT">Montana</option>
                      <option value="NE">Nebraska</option>
                      <option value="NV">Nevada</option>
                      <option value="NH">New Hampshire</option>
                      <option value="NJ">New Jersey</option>
                      <option value="NM">New Mexico</option>
                      <option value="NY">New York</option>
                      <option value="NC">North Carolina</option>
                      <option value="ND">North Dakota</option>
                      <option value="OH">Ohio</option>
                      <option value="OK">Oklahoma</option>
                      <option value="OR">Oregon</option>
                      <option value="PA">Pennsylvania</option>
                      <option value="RI">Rhode Island</option>
                      <option value="SC">South Carolina</option>
                      <option value="SD">South Dakota</option>
                      <option value="TN">Tennessee</option>
                      <option value="TX">Texas</option>
                      <option value="UT">Utah</option>
                      <option value="VT">Vermont</option>
                      <option value="VA">Virginia</option>
                      <option value="WA">Washington</option>
                      <option value="WV">West Virginia</option>
                      <option value="WI">Wisconsin</option>
                      <option value="WY">Wyoming</option>
                      <option value="DC">District of Columbia</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {currentPet.animalType === 'sa' && (
              <div className="space-y-4 pt-4 border-t border-gray-200">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs sm:text-sm text-blue-900">
                    <strong>ADA Compliance:</strong> Under the Americans with Disabilities Act, property managers may ask only two questions about Service Animals.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Is this animal required because of a disability? <span className="text-gray-500 text-xs">(Optional)</span>
                  </label>
                  <select
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors"
                    value={currentPet.saRequiredForDisability || ''}
                    onChange={(e) => handleInputChange('pet.saRequiredForDisability', e.target.value)}
                  >
                    <option value="">Select an option</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="prefer_not_to_answer">Prefer not to answer</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    This question is optional based on property management preferences
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    What work or task has this animal been trained to perform? <span className="text-gray-500 text-xs">(Optional)</span>
                  </label>
                  <textarea
                    placeholder="Describe the specific tasks this service animal has been trained to perform"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors min-h-[100px]"
                    value={currentPet.saTasksTrained || ''}
                    onChange={(e) => handleInputChange('pet.saTasksTrained', e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This question is optional based on property management preferences
                  </p>
                </div>
              </div>
            )}
          </div>
        );

      case 'vetInfo': {
        const currentVetPhoneConfig = countryPhoneConfigs.find(c => c.code === selectedVetCountryCode) || countryPhoneConfigs[0];
        const isServiceAnimal = currentPet.animalType === 'sa';
        const vetFieldIds = ['q13','q14','q15','q16'];
        const hasAnyVisibleVetField = vetFieldIds.some(id => isFieldVisible(id));
        const hasCustomVetInfoFields = getCustomQuestions('vet_info').length > 0;

        if (!hasAnyVisibleVetField && !hasCustomVetInfoFields) {
          return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Info className="w-10 h-10 text-gray-400 mb-3" />
              <p className="text-sm text-gray-600">
                This property does not require veterinary information. Please proceed to the next step.
              </p>
            </div>
          );
        }

        return (
          <div className="flex flex-col gap-4">
            <p className="text-xs sm:text-sm text-gray-600 mb-4">
              {isServiceAnimal
                ? 'Veterinary information is optional for Service Animals'
                : 'Provide your veterinarian\'s contact information'}
            </p>

            {isServiceAnimal && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs sm:text-sm text-blue-900">
                  Property managers may choose to collect veterinary information for Service Animals, but it is not required by law.
                </p>
              </div>
            )}

            {/* q13: Veterinary Clinic Name */}
            {isFieldVisible('q13') && (
            <div style={{ order: getQuestionOrder('q13') }}>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {getFieldLabel('q13', 'Veterinary Clinic Name')} {!isServiceAnimal && isFieldRequired('q13') && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                placeholder="Enter Clinic Name"
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                  validationErrors.vetClinic ? 'border-red-500' : 'border-gray-300'
                }`}
                value={currentPet.vetClinic}
                onChange={(e) => handleInputChange('pet.vetClinic', e.target.value)}
                required={!isServiceAnimal}
              />
              {validationErrors.vetClinic && (
                <p className="text-xs text-red-600 mt-1 animate-pulse">
                  {validationErrors.vetClinic}
                </p>
              )}
            </div>
            )}

            {/* q14: Vet Address */}
            {isFieldVisible('q14') && (
            <div className="relative address-autocomplete-container" style={{ order: getQuestionOrder('q14') }}>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {getFieldLabel('q14', 'Address')} {!isServiceAnimal && isFieldRequired('q14') && <span className="text-red-500">*</span>}
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Start typing clinic address..."
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                    validationErrors.vetAddress ? 'border-red-500' : 'border-gray-300'
                  }`}
                  value={currentPet.vetAddress}
                  onChange={(e) => {
                    handleInputChange('pet.vetAddress', e.target.value);
                    if (!e.target.value) {
                      setShowAddressSuggestions(false);
                    }
                  }}
                  onFocus={() => {
                    if (addressSuggestions.length > 0) {
                      setShowAddressSuggestions(true);
                    }
                  }}
                  required={!isServiceAnimal}
                />
                {addressSearchLoading && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <svg className="animate-spin h-5 w-5 text-purple-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}
              </div>
              
              {/* Address Suggestions Dropdown */}
              {showAddressSuggestions && addressSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {addressSuggestions.map((suggestion, index) => (
                    <div
                      key={`${suggestion.source}-${index}`}
                      className="p-3 hover:bg-purple-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
                      onClick={() => handleAddressSelection(suggestion)}
                    >
                      <div className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-sm text-gray-900">{suggestion.display}</p>
                          <p className="text-xs text-gray-500 mt-0.5 capitalize">{suggestion.source}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {validationErrors.vetAddress && (
                <p className="text-xs text-red-600 mt-1 animate-pulse">
                  {validationErrors.vetAddress}
                </p>
              )}
            </div>
            )}

            {/* q15: Vet Phone */}
            {isFieldVisible('q15') && (
            <div style={{ order: getQuestionOrder('q15') }}>
              <label className="block text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
                <Phone className="w-4 h-4" />
                {getFieldLabel('q15', 'Phone')} {!isServiceAnimal && isFieldRequired('q15') && <span className="text-red-500">*</span>}
              </label>

              <div className="flex flex-col sm:flex-row gap-2 min-w-0">
                <div className="relative w-full sm:w-auto flex-shrink-0">
                  <select
                    className="w-full sm:w-auto appearance-none p-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors cursor-pointer min-w-0"
                    value={selectedVetCountryCode}
                    onChange={(e) => handleCurrentVetCountryCodeChange(e.target.value)}
                  >
                    {countryPhoneConfigs.map(config => (
                      <option key={config.code} value={config.code}>
                        {config.flag} {config.code}
                      </option>
                    ))}
                  </select>
                  <Globe className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>

                <div className="flex-1 relative min-w-0">
                  <input
                    type="tel"
                    placeholder={`Enter ${currentVetPhoneConfig.maxDigits} digits`}
                    className={`w-full p-3 pr-16 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors ${
                      vetPhoneValidationError ? 'border-red-500' :
                      currentPet.vetPhoneNumber.length === currentVetPhoneConfig.maxDigits ? 'border-green-500 bg-green-50' :
                      'border-gray-300'
                    }`}
                    value={currentPet.vetPhoneNumber}
                    onChange={(e) => handleCurrentVetPhoneNumberChange(e.target.value)}
                    onBlur={validateCurrentVetPhoneNumber}
                    maxLength={currentVetPhoneConfig.maxDigits}
                    required={!isServiceAnimal}
                  />
                  <span className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-xs ${
                    currentPet.vetPhoneNumber.length === currentVetPhoneConfig.maxDigits ? 'text-green-600' :
                    currentPet.vetPhoneNumber.length >= currentVetPhoneConfig.maxDigits * 0.8 ? 'text-orange-500' :
                    'text-gray-400'
                  }`}>
                    {currentPet.vetPhoneNumber.length}/{currentVetPhoneConfig.maxDigits}
                  </span>
                </div>
              </div>

              {vetPhoneValidationError && (
                <p className="text-xs text-red-600 mt-1 animate-pulse">
                  {vetPhoneValidationError}
                </p>
              )}
            </div>
            )}

            {/* q16: Date of Last Visit */}
            {isFieldVisible('q16') && (
            <div style={{ order: getQuestionOrder('q16') }}>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {getFieldLabel('q16', 'Date of Last Visit')} {!isServiceAnimal && isFieldRequired('q16') && <span className="text-red-500">*</span>}
              </label>
              <input
                type="date"
                min={getOneYearAgoDate()}
                max={getTodayDate()}
                value={currentPet.lastVisit || ''}
                required={!isServiceAnimal}
                onChange={(e) => {
                  const selectedDateStr = e.target.value;
                  if (!selectedDateStr) {
                    handleInputChange('pet.lastVisit', '');
                    return;
                  }

                  const todayStr = getTodayDate();
                  const oneYearAgoStr = getOneYearAgoDate();

                  // Compare dates as strings (YYYY-MM-DD format)
                  if (selectedDateStr <= todayStr && selectedDateStr >= oneYearAgoStr) {
                    handleInputChange('pet.lastVisit', selectedDateStr);
                  } else {
                    // Don't update - reject the invalid date
                    e.target.value = currentPet.lastVisit || '';
                  }
                }}
                onKeyDown={(e) => e.preventDefault()}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors [&::-webkit-calendar-picker-indicator]:cursor-pointer ${
                  validationErrors.lastVisit ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {validationErrors.lastVisit && (
                <p className="text-xs text-red-600 mt-1 animate-pulse">
                  {validationErrors.lastVisit}
                </p>
              )}
            </div>
            )}

            {/* Custom admin-added questions for vet_info */}
            {renderCustomFields('vet_info')}
          </div>
        );
      }

      case 'documents': {
        // Documents step is data-driven: any question in the `documents` group with
        // type='file' and show=true renders as an upload row. ESA Letter is hardcoded
        // (option C) — always shown for ESA regardless of admin settings.
        const visibleFileDocs = (questionnaireSettings?.questionnaire || []).filter(q =>
          q.group === 'documents' &&
          q.type === 'file' &&
          shouldShowQuestion(q)
        );
        const nonFileCustomDocs = getCustomQuestions('documents').filter(q => q.type !== 'file');
        const hasESALetter = currentPet.animalType === 'esa';

        if (visibleFileDocs.length === 0 && nonFileCustomDocs.length === 0 && !hasESALetter) {
          return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Info className="w-10 h-10 text-gray-400 mb-3" />
              <p className="text-sm text-gray-600">
                This property does not require document uploads. Please proceed to the next step.
              </p>
            </div>
          );
        }
        return (
          <>
            {/* Non-file custom questions in the documents group render above the upload list */}
            {nonFileCustomDocs.length > 0 && (
              <div className="flex flex-col gap-4 mb-4 px-1">
                {renderCustomFields('documents')}
              </div>
            )}
            <DocumentUploadView
              onComplete={handleDocumentUploadComplete}
              onPrevious={handlePrevious}
              onClose={handleCloseAttempt}
              isESA={currentPet.animalType === 'esa'}
              animalType={currentPet.animalType}
              petData={{
                petName: currentPet.petName,
                petIndex: stepInfo.petIndex,
                pet_id: currentPet.id
              }}
              draftPetId={activeDraftId}
              ensureDraftPet={ensureDraftPet}
              initialDocuments={getCurrentPetDocuments()}
              onDocumentsChange={updateCurrentPetDocuments}
              questionnaireSettings={questionnaireSettings}
            />
          </>
        );
      }

      case 'attestation': {
        const currentPet = formData.pets[currentPetIndex] || formData.pets[0];
        return (
          <div className="space-y-6 p-4 sm:p-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-base font-semibold text-blue-900 mb-2">{PET_REGISTRATION_ATTESTATION_TITLE}</h3>
              <p className="text-sm text-blue-800">
                Please review the attestation below. You must check the box to confirm before submitting.
              </p>
            </div>
            <AttestationBox
              text={PET_REGISTRATION_ATTESTATION_TEXT}
              checked={attestationAcknowledged}
              onCheckedChange={setAttestationAcknowledged}
              label="I have read and agree to the above attestation."
              id="pet-registration-attestation"
              required
            />
            <div className="text-sm text-gray-600">
              <p className="font-medium text-gray-900 mb-1">Pet: {currentPet.petName}</p>
              <p>Property: {formData.property} · Unit: {formData.unit}</p>
            </div>
            {uploadBlockMessage && (
              <p className="text-sm text-red-600">{uploadBlockMessage}</p>
            )}
          </div>
        );
      }
       
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-none sm:rounded-xl shadow-2xl w-full h-full sm:h-auto sm:max-w-6xl sm:max-h-[90vh] overflow-hidden relative">
        <div className="flex flex-col md:flex-row h-full sm:h-[90vh]">
          {/* Left Sidebar - Hidden on mobile */}
          <div className="hidden md:flex md:w-64 lg:w-80 bg-gray-50 border-r border-gray-200 flex-col">
            <div className="p-4 lg:p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-6 h-6 bg-gray-100 rounded-md flex items-center justify-center">
                  <PawPrint className="w-4 h-4 text-gray-600" />
                </div>
                <span className="font-semibold text-gray-900">{getSidebarTitle()}</span>
              </div>
              <p className="text-sm text-gray-600">Use this easy wizard to help add {
                (() => {
                  const currentPet = formData.pets?.[currentPetIndex] || formData.pets?.[0];
                  const animalType = currentPet?.animalType;
                  if (animalType === 'esa') return 'emotional support animals';
                  if (animalType === 'sa') return 'service animals';
                  return 'pets';
                })()
              } to your policy!</p>
            </div>

            <div className="px-4 lg:px-6 space-y-1 flex-1">
              {(() => {
                const getStepStates = () => {
                  const propertyCompleted = step > 1;
                  
                  let petDetailsCompleted = false;
                  let esaInfoCompleted = false;
                  let vetInfoCompleted = false;
                  let documentsCompleted = false;
                  
                  if (isMultiPetWorkflow) {
                    esaInfoCompleted = step > 2;
                    petDetailsCompleted = step > 3;
                    vetInfoCompleted = step > 4;
                    documentsCompleted = step > 5;
                  } else {
                    esaInfoCompleted = step > 2;
                    petDetailsCompleted = step > 3;
                    vetInfoCompleted = step > 4;
                    documentsCompleted = step > 5;
                  }
                  
                  const currentPetForSidebar = formData.pets?.[currentPetIndex] || formData.pets?.[0];
                  const isSA = currentPetForSidebar?.animalType === 'sa';
                  
                  const steps = [
                    { 
                      label: 'Resident Information', 
                      stepNum: 1, 
                      active: step === 1, 
                      completed: propertyCompleted 
                    },
                    { 
                      label: 'Animal Type', 
                      stepNum: 2, 
                      active: step === 2, 
                      completed: esaInfoCompleted 
                    },
                    { 
                      label: getStep2Label(), 
                      stepNum: 3, 
                      active: step === 3, 
                      completed: petDetailsCompleted 
                    },
                    { 
                      label: 'Veterinary Information', 
                      stepNum: 4, 
                      active: step === 4, 
                      completed: vetInfoCompleted 
                    },
                    ...(!isSA ? [{ 
                      label: 'Documents', 
                      stepNum: 5, 
                      active: step === 5, 
                      completed: documentsCompleted 
                    }] : []),
                    { 
                      label: 'Review & Attestation', 
                      stepNum: isSA ? 5 : 6, 
                      active: step === totalSteps, 
                      completed: false 
                    }
                  ];
                  return steps;
                };
                return getStepStates();
              })().map((item, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    item.active ? 'bg-purple-100 text-purple-700' : 
                    item.completed ? 'bg-green-50 text-green-700' :
                    'text-gray-600'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                    item.active ? 'bg-[color:var(--pmc-primary)] text-white' :
                    item.completed ? 'bg-green-500 text-white' :
                    'bg-gray-300 text-gray-600'
                  }`}>
                    {item.completed ? '✓' : item.stepNum}
                  </div>
                  <span className="text-sm font-medium">{item.label}</span>
                </div>
              ))}
            </div>

            <div className="p-4 lg:p-6 pt-4">
              <div className="bg-gray-200 rounded-full h-2 mb-2">
                <div 
                  className="h-2 rounded-full transition-all duration-500 bg-[color:var(--pmc-primary)]"
                  style={{ width: `${(step / totalSteps) * 100}%` }}
                />
              </div>
              <div className="text-sm text-gray-600 font-medium">{step} of {totalSteps}</div>
            </div>
          </div>

          {/* Right Content */}
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{getCurrentStepInfo().title}</h2>
              <button
                onClick={handleCloseAttempt}
                className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Mobile Progress Indicator */}
            <div className="md:hidden px-4 py-3 bg-gray-50 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Progress</span>
                <span className="text-xs text-gray-600">{step} of {totalSteps}</span>
              </div>
              <div className="bg-gray-200 rounded-full h-2">
                <div
                  className="h-2 rounded-full transition-all duration-500 bg-[color:var(--pmc-primary)]"
                  style={{ width: `${(step / totalSteps) * 100}%` }}
                />
              </div>
            </div>

            <div ref={stepScrollRef} className="flex-1 p-4 sm:p-6 overflow-y-auto min-h-0">
              {renderStepContent()}
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 p-4 sm:p-6 border-t border-gray-200 bg-white">
              <button
                onClick={handlePrevious}
                className="w-full sm:w-auto px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors uppercase tracking-wide"
              >
                Back
              </button>
              {(() => {
                const stepInfo = getCurrentStepInfo();
                const isAttestation = stepInfo.type === 'attestation';
                const isPropertyStep = stepInfo.type === 'property';
                const submitDisabled = isSubmitting || isStep1Loading || (isAttestation && !attestationAcknowledged);
                const buttonLabel = isSubmitting
                  ? 'Submitting...'
                  : isAttestation
                    ? 'Complete Registration'
                    : isPropertyStep && userFilledMissingGCFields()
                      ? 'Update & Continue'
                      : 'Next';
                return (
                  <button
                    onClick={handleNext}
                    disabled={submitDisabled}
                    className={`w-full sm:w-auto px-6 py-2 rounded-lg font-bold transition-all uppercase tracking-wide ${
                      submitDisabled
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : 'bg-[color:var(--pmc-primary)] text-white hover:shadow-lg'
                    }`}
                  >
                    {buttonLabel}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal for Close */}
      {showConfirmClose && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md mx-4 w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Save your progress?</h3>
            <p className="text-gray-600 mb-6">
              Would you like to save your progress as a draft? You can resume later from where you left off.
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleSaveDraft}
                disabled={isSavingDraft || isDiscarding}
                className="w-full px-4 py-2.5 bg-[color:var(--pmc-primary)] text-white rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-60"
              >
                {isSavingDraft ? 'Saving...' : 'Save as Draft'}
              </button>
              <button
                onClick={() => setShowConfirmClose(false)}
                disabled={isSavingDraft || isDiscarding}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-60"
              >
                Continue Editing
              </button>
              <button
                onClick={handleConfirmCancel}
                disabled={isSavingDraft || isDiscarding}
                className="w-full px-4 py-2 text-sm text-red-600 font-medium hover:text-red-700 transition-colors disabled:opacity-60"
              >
                {isDiscarding ? 'Discarding...' : 'Discard Progress'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Reload */}
      {showReloadConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md mx-4 w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Save before refreshing?</h3>
            <p className="text-gray-600 mb-6">
              You have unsaved progress. Would you like to save it as a draft before refreshing the page?
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleReloadWithSave}
                disabled={isSavingDraft}
                className="w-full px-4 py-2.5 bg-[color:var(--pmc-primary)] text-white rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-60"
              >
                {isSavingDraft ? 'Saving...' : 'Save & Refresh'}
              </button>
              <button
                onClick={handleReloadWithoutSave}
                disabled={isSavingDraft}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-60"
              >
                Refresh Without Saving
              </button>
              <button
                onClick={() => setShowReloadConfirm(false)}
                disabled={isSavingDraft}
                className="w-full px-4 py-2 text-sm text-gray-500 font-medium hover:text-gray-700 transition-colors disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionnaireModal;